import Animation from '../ui/Animation.js';
import Status, { VolatileStatus } from '../entities/Status.js';
import BattleAction from './battle-actions/BattleAction.js';
import BattleStateMachineActionGenerator from './battle-actions/BattleStateMachineActionGenerator.js';
import ActionType from './battle-actions/ActionType.js';
import { getOSM } from '../OverworldAnimation.js';
import Move from '../entities/Move.js';
import { HazardType } from '../entities/EntryHazard.js';

// Import custom move implementations
import OdorSleuth from './moves/OdorSleuth.js';
import WorrySeed from './moves/WorrySeed.js';
import Covet from './moves/Covet.js';
import MudSport from './moves/MudSport.js';
import WaterSport from './moves/WaterSport.js';
import Flail from './moves/Flail.js';

import {
    keySPressInstant,
    keyWPressInstant,
    keyAPressInstant,
    keyDPressInstant,
    keyKPressInstant,
    keyLPressInstant,
    keyPPressInstant,
} from '../BattleAnimation.js';
import BagMenuBackEnd, { BagPage } from '../entities/menus/BagMenu.js';
import {
    SPAWN_SCALE_SPEED,
    END_BATTLE,
    POKEMON_PRESELECTED,
    BAG_ITEM_PRESELECTED,
    IT_WONT_HAVE_ANY_EFFECT,
    NEW_MOVE_AVAILABLE,
    LEARNED_NEW_MOVE,
    DID_NOT_LEARN_NEW_MOVE,
    TRAINER_BATTLE_INTRO,
    WILD_BATTLE_INTRO,
    TRAINER_WOULD_LIKE_TO_BATTLE,
    TRAINER_SWITCHES_POKEMON,
    TRAINER_POKEMON_FORCED_OUT,
    PLAYER_POKEMON_FORCED_OUT,
    FOE_POKEMON_SENT_OUT_END,
    PLAYER_POKEMON_SENT_OUT_END,
    TRAINER_SENDS_OUT_POKEMON,
    PLAYER_SENDS_OUT_POKEMON,
    CHOOSE_ACTION,
    CHOOSE_MOVE,
    NO_PP_LEFT_FOR_THIS_MOVE,
    PRE_BATTLE_SEQUENCE,
    BATTLE_SEQUENCE_PLAYER_MOVE,
    BATTLE_SEQUENCE_FOE_MOVE,
    END_BATTLE_SEQUENCE,
    FOE_FAINTS,
    SETUP_EXPERIENCE_GAIN_QUEUE,
    GAIN_EXPERIENCE_NOTIFICATION,
    GAIN_EXPERIENCE_ACTIVE_POKEMON,
    LEVEL_UP_POKEMON,
    CURRENT_MOVES_FULL,
    STOP_LEARNING_NEW_MOVE,
    CHOOSE_MOVE_TO_FORGET,
    FORGOT_MOVE,
    PLAYER_FAINTS,
    WHITED_OUT,
    END_TRAINER_BATTLE,
    RUN_AWAY,
    POKEMON_MENU_BATTLE,
    CHOOSE_NEXT_POKEMON_QUESTION,
    SWITCHED_TO_SAME_POKEMON,
    SWITCHED_TO_FAINTED_POKEMON,
    POKEMON_SWITCHED_END_OF_TURN,
    POKEMON_SWITCHED,
    SUMMARY_MENU_BATTLE,
    SUMMARY_MOVE_DETAIL,
    BAG_MENU,
    SELECT_POKEMON_TO_APPLY_ITEM,
    SELECT_MOVE_TO_APPLY_ITEM,
    ITEM_APPLIED,
    CATCH_POKEMON_ATTEMPT,
    POKEMON_CAUGHT,
    DONT_BE_A_THIEF,
    ADD_CAUGHT_POKEMON_TO_POKEDEX,
    ASK_FOR_NICKNAME,
    POKEMON_SENT_TO_PC,
    NICKNAME_INPUT,
    NICKNAME_CONFIRMATION,
    ANIMATION_SPEED,
    APPLY_ITEM_TO_POKEMON,
    stageMultiplier,
    typeEffectiveness,
    healingItemIDs,
    pokeballItemIDs,
    moveItemIDs,
    berryItemIds,
    pinchBerryStat,
    pinchBerryItemIds,
    battleItemIds,
    xItemStat,
    xItemIds,
} from '../constants.js';

// Map of move identifiers to their custom implementation classes
const customMoveImplementations = new Map([
    ['odor_sleuth', OdorSleuth],
    ['covet', Covet],
    ['worry_seed', WorrySeed],
    ['mud_sport', MudSport],
    ['water_sport', WaterSport],
    ['flail', Flail],
]);

export default class BattleStateMachine {
    constructor({ player, foePokemon, playerPokemon, foeTrainer = null, battleGUI }) {
        this.battleGUI = battleGUI;
        this.player = player;
        this.foeTrainer = foeTrainer;
        this.currentState = foeTrainer === null ? WILD_BATTLE_INTRO : TRAINER_BATTLE_INTRO;
        this.actions = [];
        this.currentAction = null;
        this.foePokemon = foeTrainer === null ? foePokemon : foeTrainer.party[0];
        this.playerPokemon = playerPokemon;
        this.playerPokemon.currentSprite = this.playerPokemon.backSprite;
        this.foePokemon.currentSprite = this.foePokemon.frontSprite;
        this.battleText = '';
        this.delay = 0;
        this.delayCursor = 0;
        this.battleOption = 0;
        this.moveOption = 0;
        this.moveItemOption = 0;
        this.pokemonOption = 0;
        this.pokemonDetailOption = 0;
        this.summaryOption = 0;
        this.showMoveSummary = false;
        this.summaryMoveDetailOption = 0;
        this.availableItems = [
            this.player.healingItemsList(),
            this.player.berriesList(),
            this.player.pokeballsList(),
            this.player.battleItemsList(),
        ];
        this.bagPage = 0;
        this.bagItem = 0;
        this.bagDetailOption = 0;
        this.selectedItem = null;
        this.yesOrNoOption = 0;
        this.pokemonMenuText = 'Choose a Pokemon.';
        this.turnQueue = [];
        this.upcomingPokemon = {};
        this.participantPokemon = new Set([this.playerPokemon]);
        this.shouldCancel = false;
        this.experienceGainQueue = [];
        this.foePokemonQueue = this.foeTrainer !== null ? [...this.foeTrainer.party] : [this.foePokemon];
        this.stateQueue = [];
        this.ag = new BattleStateMachineActionGenerator({ stateMachine: this, d: this.battleGUI.drawables });
        this.context = {
            completedHits: 0,
            weather: {
                rain: 0,
                sun: 0,
                sandstorm: 0,
                hail: 0,
            },
        };
        this.endTurnContext = {};
        this.currentTurn = 0;
        this.stateHistory = [];
        this.bagMenu = new BagMenuBackEnd({
            allowedPages: [BagPage.ITEMS, BagPage.BERRIES, BagPage.POKEBALLS, BagPage.BATTLE_ITEMS],
            stateMachine: this,
        });
        this.partyOrder = [0, 1, 2, 3, 4, 5];

        this.entryHazardsFoe = {
            [HazardType.SPIKES]: 0,
            [HazardType.TOXIC_SPIKES]: 0,
            [HazardType.STICKY_WEB]: 0,
            [HazardType.STEALTH_ROCK]: 0,
        };

        this.entryHazardsPlayer = {
            [HazardType.SPIKES]: 0,
            [HazardType.TOXIC_SPIKES]: 0,
            [HazardType.STICKY_WEB]: 0,
            [HazardType.STEALTH_ROCK]: 0,
        };

        /** @type {{ id: string, source: import('../entities/Pokemon.js').default }[]} Field effects active in battle (e.g. mud_sport, water_sport). Cleared when source switches out or faints. */
        this.fieldEffects = [];
        /** Single active weather effect (only one at a time). */
        this.weatherEffect = null;
    }

    /**
     * Add a field effect tied to the given Pokémon. Effect lasts until that Pokémon switches out or faints.
     * @param {string} fieldEffectId - e.g. 'mud_sport', 'water_sport'
     * @param {import('../entities/Pokemon.js').default} source - Pokémon that used the move
     */
    addFieldEffect(fieldEffectId, source) {
        this.fieldEffects.push({ id: fieldEffectId, source });
    }

    /**
     * Remove all field effects whose source is the given Pokémon (e.g. when they switch out or faint).
     * @param {import('../entities/Pokemon.js').default} pokemon - Pokémon leaving the field
     */
    clearFieldEffectsForSource(pokemon) {
        this.fieldEffects = this.fieldEffects.filter((e) => e.source !== pokemon);
    }

    /**
     * Called by Move.getPower() to allow custom implementations (e.g. Flail) to override power.
     * @returns {number | undefined} Override power, or undefined to use move.power
     */
    getMovePower(move, user, target) {
        const CustomMove = customMoveImplementations.get(move?.identifier);
        if (typeof CustomMove?.getEffectivePower === 'function') {
            return CustomMove.getEffectivePower(move, user, target, this);
        }
        return undefined;
    }

    /**
     * Called by Move.getAccuracy() to allow custom implementations to override accuracy.
     * @returns {number | undefined} Override accuracy, or undefined to use move.accuracy
     */
    getMoveAccuracy(move, user, target) {
        const CustomMove = customMoveImplementations.get(move?.identifier);
        if (typeof CustomMove?.getAccuracy === 'function') {
            return CustomMove.getAccuracy(move, user, target, this);
        }
        return undefined;
    }

    /**
     * Set or increment an entry hazard on the player's side (affects player Pokemon).
     * @param {HazardType} hazardType - The type of entry hazard
     * @param {number} layers - Number of layers to add (default: 1)
     * @returns {boolean} True if the hazard was successfully set/incremented, false if it's already at max
     */
    setPlayerEntryHazard(hazardType, layers = 1) {
        const hazards = this.entryHazardsPlayer;
        const currentLayers = hazards[hazardType] || 0;

        // Define maximum layers for each hazard type
        const maxLayers = {
            [HazardType.SPIKES]: 3,
            [HazardType.TOXIC_SPIKES]: 2,
            [HazardType.STEALTH_ROCK]: 1,
            [HazardType.STICKY_WEB]: 1,
        };

        const max = maxLayers[hazardType] || 1;
        const newLayers = Math.min(currentLayers + layers, max);

        if (newLayers === currentLayers && currentLayers >= max) {
            // Already at max, can't add more
            return false;
        }

        hazards[hazardType] = newLayers;
        return true;
    }

    /**
     * Set or increment an entry hazard on the foe's side (affects foe Pokemon).
     * @param {HazardType} hazardType - The type of entry hazard
     * @param {number} layers - Number of layers to add (default: 1)
     * @returns {boolean} True if the hazard was successfully set/incremented, false if it's already at max
     */
    setFoeEntryHazard(hazardType, layers = 1) {
        const hazards = this.entryHazardsFoe;
        const currentLayers = hazards[hazardType] || 0;

        // Define maximum layers for each hazard type
        const maxLayers = {
            [HazardType.SPIKES]: 3,
            [HazardType.TOXIC_SPIKES]: 2,
            [HazardType.STEALTH_ROCK]: 1,
            [HazardType.STICKY_WEB]: 1,
        };

        const max = maxLayers[hazardType] || 1;
        const newLayers = Math.min(currentLayers + layers, max);

        if (newLayers === currentLayers && currentLayers >= max) {
            // Already at max, can't add more
            return false;
        }

        hazards[hazardType] = newLayers;
        return true;
    }

    /**
     * Clear a specific entry hazard or all entry hazards on the player's side.
     * @param {HazardType|null} hazardType - The type of entry hazard to clear, or null to clear all
     */
    clearPlayerEntryHazard(hazardType) {
        const hazards = this.entryHazardsPlayer;

        if (hazardType === null || hazardType === undefined) {
            // Clear all hazards
            hazards[HazardType.SPIKES] = 0;
            hazards[HazardType.TOXIC_SPIKES] = 0;
            hazards[HazardType.STICKY_WEB] = 0;
            hazards[HazardType.STEALTH_ROCK] = 0;
        } else {
            // Clear specific hazard
            hazards[hazardType] = 0;
        }
    }

    /**
     * Clear a specific entry hazard or all entry hazards on the foe's side.
     * @param {HazardType|null} hazardType - The type of entry hazard to clear, or null to clear all
     */
    clearFoeEntryHazard(hazardType) {
        const hazards = this.entryHazardsFoe;

        if (hazardType === null || hazardType === undefined) {
            // Clear all hazards
            hazards[HazardType.SPIKES] = 0;
            hazards[HazardType.TOXIC_SPIKES] = 0;
            hazards[HazardType.STICKY_WEB] = 0;
            hazards[HazardType.STEALTH_ROCK] = 0;
        } else {
            // Clear specific hazard
            hazards[hazardType] = 0;
        }
    }

    /**
     * Clear all entry hazards on both sides.
     */
    clearAllEntryHazards() {
        this.clearPlayerEntryHazard(null);
        this.clearFoeEntryHazard(null);
    }

    /**
     * Get the current number of layers for a specific entry hazard on the player's side.
     * @param {HazardType} hazardType - The type of entry hazard
     * @returns {number} The number of layers (0 if not set)
     */
    getPlayerEntryHazardLayers(hazardType) {
        return this.entryHazardsPlayer[hazardType] || 0;
    }

    /**
     * Get the current number of layers for a specific entry hazard on the foe's side.
     * @param {HazardType} hazardType - The type of entry hazard
     * @returns {number} The number of layers (0 if not set)
     */
    getFoeEntryHazardLayers(hazardType) {
        return this.entryHazardsFoe[hazardType] || 0;
    }

    setUpBattleActionsContext(move, attacker, defender) {
        this.context = {
            ...this.context,
            numHits: 0,
            completedHits: 0,
            moveEventTexts: [],
            criticalHit: false,
            damageDealt: 0,
            damageRecovered: 0,
            recoilDamage: 0,
            animations: [],
            move: move,
            attacker: attacker,
            defender: defender,
            attackerFainted: attacker.stats.hp === 0,
            defenderFainted: defender.stats.hp === 0,
            actions: [],
            currentVolatileStatus: null,
            statusChance: null,
            statusTurns: null,
            shouldVolatileStatusEffectApplicationSucceed: true,
        };
    }

    shouldRemoveAttackerStatus() {
        return (
            !!this.context.attacker.status1 &&
            this.context.attacker.status1.currentTurn > this.context.attacker.status1.duration
        );
    }

    removeAttackerStatus() {
        if (this.shouldRemoveAttackerStatus()) {
            this.context.actions.push(
                this.ag.readText(this.context.attacker.status1.disaffectedText, { delay: 50 })
            );
            this.context.actions.push(
                this.ag.script(() => {
                    this.context.attacker.status1 = null;
                })
            );
        }

        // Check for confusion
        this.context.actions.push(
            this.ag.script(() => {
                this.context.attacker.updateVolatileStatusTurns();
            })
        );
        // this.context.attacker.updateVolatileStatusTurns();
        if (this.context.attacker.shouldSnapOutOfConfusion()) {
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name} snapped out of confusion!`, { delay: 50 })
            );
        }
    }

    shouldStatusPreventAttackerMove() {
        return (
            !this.shouldRemoveAttackerStatus() &&
            !!this.context.attacker.status1 &&
            this.context.attacker.status1.movePreventionChance > Math.random()
        );
    }

    preventAttackerMoveDueToStatus() {
        if (this.context.attacker.isConfused()) {
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name} is confused.`, { delay: 50 })
            );
            if (Math.random() > 0.5) {
                this.context.actions.push(this.ag.readText('It hurt itself in its confusion.'));
                this.animateFlicker(this.context.attacker);
                this.context.actions.push(
                    this.ag.script(() => {
                        this.context.defender = this.context.attacker;
                        this.context.move = Move.getById('hit_self');
                        this.calculateDamageDealt();
                        this.dealDamage();
                    })
                );
                return true;
            }
        }

        if (this.shouldStatusPreventAttackerMove()) {
            this.context.actions.push(
                this.ag.readText(this.context.attacker.status1.startTurnEffectText, { delay: 50 })
            );
            return true;
        }
        return false;
    }

    announceMoveUsage() {
        this.context.actions.push(
            this.ag.readText(`${this.context.attacker.name} used ${this.context.move.name}!`, { delay: 20 })
        );
    }

    reduceMovePP() {
        this.context.actions.push(
            this.ag.script(() => {
                this.context.move.pp -= 1;
            })
        );
    }

    calculateMoveFails() {
        if (this.context.move.invalidAfterTurn < this.currentTurn) {
            return true;
        }
        return false;
    }

    shouldMoveHitTarget() {
        const moveFails = this.calculateMoveFails();
        if (moveFails) {
            this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
            return false;
        }

        const moveHitsTarget = this.calculateMoveHits(
            this.context.move,
            this.context.attacker,
            this.context.defender
        );
        if (!moveHitsTarget) {
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name}'s attack missed.`, { delay: 50 })
            );
            return false;
        }
        return true;
    }

    calculateNumberOfHits() {
        this.context.numHits =
            this.context.move.minHits +
            Math.round(Math.random() * (this.context.move.maxHits - this.context.move.minHits));
        this.context.completedHits = 0;
    }

    animateFlicker(pokemon) {
        this.context.actions.push(
            new BattleAction({
                type: ActionType.CODE,
                description: 'Animate basic player hit',
                delay: 30,
                startAction: () => {
                    this.context.animations = [
                        [
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                            },
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                            },
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                            },
                        ],
                    ];
                },
                action: () => {
                    if (this.context.animations.length > 0) {
                        const currentAnimation = this.context.animations.shift();
                        currentAnimation.forEach((animation) => {
                            pokemon.addAnimation(animation);
                        });
                    }
                    return this.context.animations.length === 0 && pokemon.getAnimationQueue().length === 0;
                },
                endAction: () => {},
            })
        );
    }

    animateDealDamage() {
        // Pokemon flickers just before damage is dealt to it
        this.context.actions.push(
            new BattleAction({
                type: ActionType.CODE,
                description: 'Animate basic player hit',
                delay: 30,
                startAction: () => {
                    this.context.animations = [
                        [
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                            {
                                animationFunction: Animation.hide,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                            {
                                animationFunction: Animation.show,
                                speed: 1,
                                duration: 5,
                                pokemon: this.context.defender,
                            },
                        ],
                    ];
                },
                action: () => {
                    if (
                        this.context.attacker.getAnimationQueue().length === 0 &&
                        this.context.defender.getAnimationQueue().length === 0
                    ) {
                        if (this.context.animations.length > 0) {
                            const currentAnimation = this.context.animations.shift();
                            currentAnimation.forEach((animation) => {
                                if (this.context.attacker === animation.pokemon) {
                                    this.context.attacker.addAnimation(animation);
                                }
                                if (this.context.defender === animation.pokemon) {
                                    this.context.defender.addAnimation(animation);
                                }
                            });
                        }
                    }

                    return (
                        this.context.animations.length === 0 &&
                        this.context.attacker.getAnimationQueue().length === 0 &&
                        this.context.defender.getAnimationQueue().length === 0
                    );
                },
                endAction: () => {},
            })
        );
    }

    animateAttack() {
        this.context.actions.push(
            new BattleAction({
                type: ActionType.CODE,
                description: 'Animate basic player attack',
                delay: 30,
                startAction: () => {
                    this.context.animations = this.context.move.getAnimation(
                        this.context.attacker,
                        this.context.defender,
                        this.playerPokemon
                    );
                },
                action: () => {
                    if (
                        this.context.attacker.getAnimationQueue().length === 0 &&
                        this.context.defender.getAnimationQueue().length === 0
                    ) {
                        if (this.context.animations.length > 0) {
                            const currentAnimation = this.context.animations.shift();
                            currentAnimation.forEach((animation) => {
                                if (this.context.attacker === animation.pokemon) {
                                    this.context.attacker.addAnimation(animation);
                                }
                                if (this.context.defender === animation.pokemon) {
                                    this.context.defender.addAnimation(animation);
                                }
                            });
                        }
                    }
                    return (
                        this.context.animations.length === 0 &&
                        this.context.attacker.getAnimationQueue().length === 0 &&
                        this.context.defender.getAnimationQueue().length === 0
                    );
                },
                endAction: () => {},
            })
        );

        if (this.context.move.category === 'physical' || this.context.move.category === 'special') {
            this.context.actions.push(
                this.ag.playAudio(`audio/sfx/tackle.mp3`),
                this.ag.delay(20),
                this.ag.playAudio(`audio/sfx/pokemon_hit.mp3`)
            );
            this.animateDealDamage();
        }
    }

    calculateDamageDealt() {
        // Base power from move (Move.getPower uses context.getMovePower for custom moves e.g. Flail)
        let effectivePower = this.context.move.getPower(this.context.attacker, this.context.defender, this);
        // Apply field effects that halve base power (Mud Sport: Electric, Water Sport: Fire)
        let damageOptions = {};
        if (effectivePower > 0 && !this.context.move.oneHitKO) {
            const hasMudSport = this.fieldEffects.some((e) => e.id === 'mud_sport');
            const hasWaterSport = this.fieldEffects.some((e) => e.id === 'water_sport');
            if (hasMudSport && this.context.move.type === 'electric') {
                effectivePower = Math.floor(effectivePower / 2);
            }
            if (hasWaterSport && this.context.move.type === 'fire') {
                effectivePower = Math.floor(effectivePower / 2);
            }
            if (effectivePower !== this.context.move.power) {
                damageOptions = { effectivePower };
            }
        }
        const { result, summary } = this.context.move.calculateDamage(
            this.context.attacker,
            this.context.defender,
            damageOptions
        );

        // Check for battle cheats: Player Move Always KO, Foe Move Always KO, and Critical Hit cheats
        const optionsMenu = getOSM()?.optionsMenu;
        const isPlayerAttacking = this.context.attacker === this.playerPokemon;
        const isFoeAttacking = this.context.attacker === this.foePokemon;

        // Determine critical hit status (can be overridden by cheats)
        let isCritical = summary.isCritical;

        // Player move always critical hit cheat
        if (isPlayerAttacking && optionsMenu?.battleCheatPlayerMoveAlwaysCriticalHit === 'on') {
            isCritical = true;
        }

        // Foe move always critical hit cheat
        if (isFoeAttacking && optionsMenu?.battleCheatFoeMoveAlwaysCriticalHit === 'on') {
            isCritical = true;
        }

        // Override damage if player move always KO cheat is enabled and player is attacking
        if (isPlayerAttacking && optionsMenu?.battleCheatPlayerMoveAlwaysKO === 'on') {
            this.context.damageDealt = this.context.defender.stats.hp;
            this.context.criticalHit = isCritical;
            this.context.damageRecovered = summary.hpRecovered;
            this.context.recoilDamage = summary.recoilDamage;
            return;
        }

        // Override damage if foe move always KO cheat is enabled and foe is attacking
        if (isFoeAttacking && optionsMenu?.battleCheatFoeMoveAlwaysKO === 'on') {
            this.context.damageDealt = this.context.defender.stats.hp;
            this.context.criticalHit = isCritical;
            this.context.damageRecovered = summary.hpRecovered;
            this.context.recoilDamage = summary.recoilDamage;
            return;
        }

        // Normal damage calculation (with potential critical hit override)
        this.context.damageDealt = result;
        this.context.criticalHit = isCritical;
        this.context.damageRecovered = summary.hpRecovered;
        this.context.recoilDamage = summary.recoilDamage;
    }

    calculateHPRecovered() {
        // TODO: Custom recoveries, such as recover, rest, wish, etc...
        this.context.damageRecovered = Math.ceil(
            this.context.damageDealt * this.context.move.recoveryPercentage
        );
    }

    calculateRecoilDamageReceived() {
        this.context.recoilDamage = Math.ceil(this.context.damageDealt * this.context.move.recoilPercentage);
    }

    dealDamage() {
        if (
            this.context.move.getPower(this.context.attacker, this.context.defender, this) > 0 ||
            this.context.move.oneHitKO
        ) {
            this.context.actions.push(this.updateHP(this.context.defender, this.context.damageDealt * -1));

            this.context.actions.push(
                this.ag.script(() => {
                    this.context.completedHits += 1;
                })
            );
            if (this.context.criticalHit) {
                this.context.actions.push(this.ag.readText('A critical hit!', { delay: 50 }));
            }
            if (this.context.move.oneHitKO) {
                this.context.actions.push(this.ag.readText("It's a one-hit KO!", { delay: 50 }));
            }

            const effectivenessText = this.effectivenessBattleText(this.context.move, this.context.defender);
            if (effectivenessText.length > 0) {
                this.context.actions.push(this.ag.readText(effectivenessText, { delay: 50 }));
            }

            this.context.actions.push(this.ag.heldBerryConsumptionAfterDamageAction());

            //
            if (this.context.move.hasPrimaryStatusEffect() && !this.context.defender.status1) {
                if (
                    this.context.move.statusEffect.receiver.chance > Math.random() &&
                    this.context.defender.stats.hp > 0
                ) {
                    const statusId = this.context.move.statusEffect.receiver.status;
                    const defender = this.context.defender;
                    this.context.actions.push(
                        this.ag.script(() => {
                            if (defender.stats.hp > 0) {
                                if (statusId === 'sleep' && defender.ability?.identifier === 'insomnia') {
                                    this.context.actions.push(
                                        this.ag.readText(
                                            `${defender.name}'s Insomnia prevented it from falling asleep!`,
                                            { delay: 50 }
                                        )
                                    );
                                    return;
                                }
                                const newStatus = new Status({
                                    id: statusId,
                                    startTurn: 0,
                                    duration: Status.getDuration(statusId),
                                    pokemon: defender,
                                });
                                this.context.actions.push(
                                    ...this.ag.applyStatusThenHeldBerryCureActions(
                                        defender,
                                        newStatus,
                                        newStatus.affectedText,
                                        statusId
                                    )
                                );
                            }
                        })
                    );
                }
            }
        }
    }

    readVolatileStatusEffectApplicationText(currentVolatileStatus, affectedPokemon) {
        switch (currentVolatileStatus) {
            case 'confusion':
                this.context.actions.push(
                    this.ag.readText(`${affectedPokemon.name} became confused!`, { delay: 50 })
                );
                break;
            case 'pumped':
                this.context.actions.push(
                    this.ag.readText(`${affectedPokemon.name} is getting pumped!`, { delay: 50 })
                );
                break;
            case 'rooted':
                this.context.actions.push(this.ag.readText(`${affectedPokemon.name} planted its roots!`));
                this.context.actions.push(this.ag.delay(50));
                break;
            case 'aqua_ring':
                this.context.actions.push(
                    this.ag.readText(`${affectedPokemon.name} surounded itself with a veil of water!`)
                );
                this.context.actions.push(this.ag.delay(50));
                break;
            case 'seeded':
                this.context.actions.push(this.ag.readText(`${affectedPokemon.name} was seeded!`));
                this.context.actions.push(this.ag.delay(50));
                break;
            case 'protect':
                this.context.actions.push(this.ag.readText(`${affectedPokemon.name} protected itself!`));
                this.context.actions.push(this.ag.delay(50));
                break;
        }
    }

    readVolatileStatusEffecctAlreadyAppliedText(currentVolatileStatus, affectedPokemon) {
        switch (currentVolatileStatus) {
            case 'confusion':
                this.context.actions.push(
                    this.ag.readText(`${affectedPokemon.name} is already confused!`, { delay: 50 })
                );
                break;
            case 'pumped':
                this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
                break;
            case 'aqua_ring':
            case 'rooted':
                this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
                break;
            case 'seeded':
                this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
                break;
            default:
                this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
                break;
        }
    }

    isTrainerBattle() {
        return this.foeTrainer !== null;
    }

    calculateVolatileStatusEffectApplicationSuccess() {
        this.context.currentVolatileStatus = null;
        if (this.context.move.volatileStatusEffect?.user?.status) {
            this.context.currentVolatileStatus = this.context.move.volatileStatusEffect.user.status;
            this.context.statusChance = this.context.move.volatileStatusEffect.user.chance;
            this.context.statusTurns = VolatileStatus.getTurns(this.context.currentVolatileStatus);
            if (this.context.attacker.stats.hp === 0) {
                return false;
            }
        }

        if (this.context.move.volatileStatusEffect?.receiver?.status) {
            this.context.currentVolatileStatus = this.context.move.volatileStatusEffect.receiver.status;
            this.context.statusChance = this.context.move.volatileStatusEffect.receiver.chance;
            this.context.statusTurns = VolatileStatus.getTurns(this.context.currentVolatileStatus);
            if (this.context.defender.stats.hp === 0) {
                return false;
            }
        }

        if (this.context.currentVolatileStatus === null || this.context.currentVolatileStatus === undefined) {
            this.context.shouldVolatileStatusEffectApplicationSucceed = true;
            return true;
        }
        switch (this.context.currentVolatileStatus) {
            case 'protect': {
                const protectHistory = this.context.attacker.inBattleStatusHistory.map((h) => h.protect);
                const protectChanceModifier =
                    1 * (1 / 3) ** protectHistory.splice(0, protectHistory.indexOf(0)).length;
                this.context.shouldVolatileStatusEffectApplicationSucceed =
                    Math.random() < this.context.statusChance * protectChanceModifier;
                break;
            }
            case 'forced_out': {
                let foeQueue = this.player.party;
                if (this.context.attacker === this.playerPokemon) {
                    foeQueue = this.foePokemonQueue;
                }
                const onlyOnePokemonRemaining = foeQueue.filter((p) => p.stats.hp > 0).length === 1;

                const attackerIsStronger = this.context.attacker.level > this.context.defender.level;
                let forceOutShouldSucceed = this.isTrainerBattle() ? true : attackerIsStronger;
                if (onlyOnePokemonRemaining) {
                    if (this.isTrainerBattle()) {
                        forceOutShouldSucceed = false;
                    }
                }
                this.context.shouldVolatileStatusEffectApplicationSucceed = forceOutShouldSucceed;
                break;
            }
            case 'confusion':
                this.context.shouldVolatileStatusEffectApplicationSucceed =
                    this.context.defender.volatileStatuses.confusion <= 0;
                break;
            case 'seeded':
                this.context.shouldVolatileStatusEffectApplicationSucceed =
                    this.context.defender.volatileStatuses.seeded <= 0;
                break;
            case 'rooted':
                this.context.shouldVolatileStatusEffectApplicationSucceed =
                    this.context.attacker.volatileStatuses.rooted <= 0;
                break;
            case 'aqua_ring':
                this.context.shouldVolatileStatusEffectApplicationSucceed =
                    this.context.attacker.volatileStatuses.aqua_ring <= 0;
                break;
            default:
                this.context.shouldVolatileStatusEffectApplicationSucceed = true;
                return true;
        }
    }

    applyVolatileStatusEffect() {
        if (this.context.move.hasVolatileStatusEffect()) {
            if (this.context.move.volatileStatusEffect.user?.status) {
                if (Math.random() < this.context.statusChance) {
                    if (this.context.shouldVolatileStatusEffectApplicationSucceed) {
                        this.context.attacker.volatileStatuses[this.context.currentVolatileStatus] =
                            this.context.statusTurns;
                        this.readVolatileStatusEffectApplicationText(
                            this.context.currentVolatileStatus,
                            this.context.attacker
                        );
                    } else {
                        this.readVolatileStatusEffecctAlreadyAppliedText(
                            this.context.currentVolatileStatus,
                            this.context.attacker
                        );
                    }
                }
            }

            // Defender Effect
            if (this.context.move.volatileStatusEffect.receiver?.status) {
                if (this.context.currentVolatileStatus === 'confusion') {
                    this.context.actions.push(
                        this.ag.heldBerryConfusionCureOrApplyAction(
                            this.context.defender,
                            this.context.statusTurns,
                            this.context.statusChance
                        )
                    );
                } else if (Math.random() < this.context.statusChance) {
                    if (this.context.shouldVolatileStatusEffectApplicationSucceed) {
                        this.context.defender.volatileStatuses[this.context.currentVolatileStatus] =
                            this.context.statusTurns;
                        this.readVolatileStatusEffectApplicationText(
                            this.context.currentVolatileStatus,
                            this.context.defender
                        );
                    } else {
                        this.readVolatileStatusEffecctAlreadyAppliedText(
                            this.context.currentVolatileStatus,
                            this.context.defender
                        );
                    }
                }
            }
        }
    }

    readEntryHazardApplicationText(entryHazard) {
        const hazardTexts = {
            [HazardType.SPIKES]: "Spikes were scattered all around the foe's feet!",
            [HazardType.STEALTH_ROCK]: "Pointed stones float in the air around the foe's team!",
            [HazardType.TOXIC_SPIKES]: "Toxic spikes were scattered all around the foe's feet!",
            [HazardType.STICKY_WEB]: "A sticky web has been laid out beneath the foe's team!",
        };
        const currentHazardText = hazardTexts[entryHazard] || `${entryHazard} was set!`;
        this.context.actions.push(this.ag.readText(currentHazardText, { delay: 50 }));
    }

    applyNonVolatileStatusEffect() {
        if (this.context.move.hasPrimaryStatusEffect() && this.context.move.category === 'status') {
            if (!this.context.defender.status1) {
                const statusId = this.context.move.statusEffect.receiver.status;
                if (statusId === 'sleep' && this.context.defender.ability?.identifier === 'insomnia') {
                    this.context.actions.push(
                        this.ag.readText(
                            `${this.context.defender.name}'s Insomnia prevented it from falling asleep!`,
                            { delay: 50 }
                        )
                    );
                } else {
                    const newStatus = new Status({
                        id: statusId,
                        startTurn: 0,
                        duration: Status.getDuration(statusId),
                        pokemon: this.context.defender,
                    });
                    this.context.actions.push(
                        ...this.ag.applyStatusThenHeldBerryCureActions(
                            this.context.defender,
                            newStatus,
                            newStatus.affectedText,
                            statusId
                        )
                    );
                }
            } else if (this.context.defender.status1.id === this.context.move.statusEffect.receiver.status) {
                this.context.actions.push(
                    this.ag.readText(this.context.defender.status1.alreadyAffectedText, { delay: 50 })
                );
            } else {
                this.context.actions.push(this.ag.readText('But it failed!'));
            }
        }

        // Effect Spore ability: 10% chance to apply sleep, paralysis, or poison when hit by or hitting with a contact move
        this.context.actions.push(
            ...this.ag.applyEffectSporeEffect(this.context.attacker, this.context.defender, this.context.move)
        );

        // Poison Point ability: 30% chance to poison when hit by or hitting with a contact move (doesn't affect Poison/Steel types)
        this.context.actions.push(
            ...this.ag.applyPoisonPointEffect(this.context.attacker, this.context.defender, this.context.move)
        );
    }

    applyEntryHazards() {
        this.context.actions.push(
            this.ag.script(() => {
                if (!this.context.move.getEntryHazard()) {
                    return false;
                }
                if (this.context.attacker === this.playerPokemon) {
                    console.log('Player set a hazard');
                    this.setFoeEntryHazard(this.context.move.getEntryHazard());
                } else if (this.context.attacker === this.foePokemon) {
                    console.log('Foe set a hazard');
                    this.setPlayerEntryHazard(this.context.move.getEntryHazard());
                } else {
                    console.error('Something isn’t quite right...');
                    return;
                }
                this.readEntryHazardApplicationText(this.context.move.getEntryHazard());
                return false;
            })
        );
    }

    applyStatModifiers() {
        if (this.context.move.hasNonZeroStat()) {
            const hasUserStats = this.context.move.hasUserStatModifiers();
            const hasReceiverStats = this.context.move.hasReceiverStatModifiers();

            this.context.actions.push(
                new BattleAction({
                    type: ActionType.CODE,
                    description: 'Apply stat modifiers',
                    action: () => {
                        // Check if user stat modifiers should be applied (check probability when action executes)
                        const shouldApplyUserStats =
                            hasUserStats &&
                            (this.context.move.userEffectProbability === null ||
                                this.context.move.userEffectProbability === undefined ||
                                Math.random() < this.context.move.userEffectProbability);

                        // Check if receiver stat modifiers should be applied (check probability when action executes)
                        const shouldApplyReceiverStats =
                            hasReceiverStats &&
                            (this.context.move.targetEffectProbability === null ||
                                this.context.move.targetEffectProbability === undefined ||
                                Math.random() < this.context.move.targetEffectProbability);

                        // Store flags for use in animation/text sections
                        this.context.shouldApplyUserStatModifiers = shouldApplyUserStats;
                        this.context.shouldApplyReceiverStatModifiers = shouldApplyReceiverStats;

                        function updateStage(stage, amount) {
                            stage += amount;
                            if (stage > 0) {
                                stage = Math.min(6, stage);
                            } else {
                                stage = Math.max(-6, stage);
                            }
                            return stage;
                        }

                        // Apply user stat modifiers only if probability check passed
                        if (shouldApplyUserStats) {
                            this.context.attacker.battleStages.attack = updateStage(
                                this.context.attacker.battleStages.attack,
                                this.context.move.statModifier.user.stat.attack
                            );
                            this.context.attacker.battleStages.defense = updateStage(
                                this.context.attacker.battleStages.defense,
                                this.context.move.statModifier.user.stat.defense
                            );
                            this.context.attacker.battleStages.specialAttack = updateStage(
                                this.context.attacker.battleStages.specialAttack,
                                this.context.move.statModifier.user.stat.specialAttack
                            );
                            this.context.attacker.battleStages.specialDefense = updateStage(
                                this.context.attacker.battleStages.specialDefense,
                                this.context.move.statModifier.user.stat.specialDefense
                            );
                            this.context.attacker.battleStages.speed = updateStage(
                                this.context.attacker.battleStages.speed,
                                this.context.move.statModifier.user.stat.speed
                            );
                            this.context.attacker.battleStages.accuracy = updateStage(
                                this.context.attacker.battleStages.accuracy,
                                this.context.move.statModifier.user.stat.accuracy
                            );
                            this.context.attacker.battleStages.evasiveness = updateStage(
                                this.context.attacker.battleStages.evasiveness,
                                this.context.move.statModifier.user.stat.evasiveness
                            );
                        }

                        // Apply receiver stat modifiers only if probability check passed
                        if (shouldApplyReceiverStats) {
                            this.context.defender.battleStages.attack = updateStage(
                                this.context.defender.battleStages.attack,
                                this.context.move.statModifier.receiver.stat.attack
                            );
                            this.context.defender.battleStages.defense = updateStage(
                                this.context.defender.battleStages.defense,
                                this.context.move.statModifier.receiver.stat.defense
                            );
                            this.context.defender.battleStages.specialAttack = updateStage(
                                this.context.defender.battleStages.specialAttack,
                                this.context.move.statModifier.receiver.stat.specialAttack
                            );
                            this.context.defender.battleStages.specialDefense = updateStage(
                                this.context.defender.battleStages.specialDefense,
                                this.context.move.statModifier.receiver.stat.specialDefense
                            );
                            this.context.defender.battleStages.speed = updateStage(
                                this.context.defender.battleStages.speed,
                                this.context.move.statModifier.receiver.stat.speed
                            );
                            this.context.defender.battleStages.accuracy = updateStage(
                                this.context.defender.battleStages.accuracy,
                                this.context.move.statModifier.receiver.stat.accuracy
                            );
                            this.context.defender.battleStages.evasiveness = updateStage(
                                this.context.defender.battleStages.evasiveness,
                                this.context.move.statModifier.receiver.stat.evasiveness
                            );
                        }

                        return true;
                    },
                })
            );

            // Animate stat changes and show text only if modifiers were actually applied
            // Create an action that runs after the stat modification action to check flags
            this.context.actions.push(
                new BattleAction({
                    type: ActionType.CODE,
                    description: 'Animate and show stat modifier text',
                    action: () => {
                        // Only proceed if modifiers were actually applied
                        if (
                            !this.context.shouldApplyUserStatModifiers &&
                            !this.context.shouldApplyReceiverStatModifiers
                        ) {
                            return true;
                        }

                        const statModificationResult = this.context.move.getStatModificationResult(
                            this.context.attacker,
                            this.context.defender
                        );

                        // Animate stat changes for attacker (only if user stats were applied)
                        if (
                            this.context.shouldApplyUserStatModifiers &&
                            statModificationResult.pokemon === this.context.attacker &&
                            this.context.attacker.stats.hp > 0
                        ) {
                            // Transform statModificationResult into simple object format { stat: stages }
                            const statModifications = {};
                            statModificationResult.attackerStats.forEach((stat) => {
                                const statChange = this.context.move.statModifier.user.stat[stat];
                                if (statChange !== 0) {
                                    statModifications[stat] = statChange;
                                }
                            });
                            if (Object.keys(statModifications).length > 0) {
                                this.context.actions.push(
                                    ...this.ag.animateStatChange(this.context.attacker, statModifications)
                                );
                            }
                        }

                        // Animate stat changes for defender (only if receiver stats were applied)
                        if (
                            this.context.shouldApplyReceiverStatModifiers &&
                            statModificationResult.pokemon === this.context.defender &&
                            this.context.defender.stats.hp > 0
                        ) {
                            // Transform statModificationResult into simple object format { stat: stages }
                            const statModifications = {};
                            statModificationResult.defenderStats.forEach((stat) => {
                                const statChange = this.context.move.statModifier.receiver.stat[stat];
                                if (statChange !== 0) {
                                    statModifications[stat] = statChange;
                                }
                            });
                            if (Object.keys(statModifications).length > 0) {
                                this.context.actions.push(
                                    ...this.ag.animateStatChange(this.context.defender, statModifications)
                                );
                            }
                        }

                        // Show stat modification text (only if modifiers were actually applied)
                        if (
                            this.context.shouldApplyUserStatModifiers ||
                            this.context.shouldApplyReceiverStatModifiers
                        ) {
                            this.context.move
                                .getStatModificationText(this.context.attacker, this.context.defender)
                                .forEach((text) => {
                                    this.context.actions.push(this.ag.readText(text, { delay: 50 }));
                                });
                        }

                        return true;
                    },
                })
            );
        }
    }

    recoverHP() {
        if (
            this.context.move.recoveryPercentage > 0 &&
            this.context.attacker.stats.hp < this.context.attacker.stats.maxHp
        ) {
            this.context.actions.push(
                new BattleAction({
                    type: ActionType.ANIMATION,
                    description: 'Animate foe health bar increase',
                    animationFunction: () => {
                        if (this.context.attacker.stats.hp < this.context.attacker.stats.maxHp) {
                            this.context.damageRecovered -= 0.5;
                            this.context.attacker.stats.hp += 0.5;
                        }
                        return (
                            this.context.damageRecovered === 0 ||
                            this.context.attacker.stats.hp === this.context.attacker.stats.maxHp
                        );
                    },
                })
            );
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name} regained health!`, { delay: 50 })
            );
        }
    }

    dealRecoilDamage() {
        if (this.context.move.recoilPercentage > 0) {
            this.context.actions.push(
                new BattleAction({
                    type: ActionType.ANIMATION,
                    description: 'Animate foe health bar decrease due to recoil.',
                    animationFunction: () => {
                        if (this.context.attacker.stats.hp > 0) {
                            this.context.recoilDamage -= 0.5;
                            this.context.attacker.stats.hp -= 0.5;
                        }
                        return this.context.recoilDamage === 0 || this.context.attacker.stats.hp === 0;
                    },
                })
            );
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name} hurt itself by recoil.`, { delay: 50 })
            );
        }
    }

    clearActionsIfDefenderFainted() {
        this.context.actions.push(
            new BattleAction({
                type: ActionType.CODE,
                description: 'Clearing out actions because defender fainted.',
                action: () => {
                    if (this.context.defender.stats.hp === 0) {
                        const targetIndex =
                            this.actions.findIndex((obj) => obj.description === 'Attack completed.') - 1;
                        if (targetIndex !== -1) {
                            this.actions = this.actions.slice(targetIndex);
                        }

                        // Clear actions that should be removed when defender faints
                        this.actions = this.actions.filter(
                            (action) => !(this.context.defender === action.pokemon && action.clearOnFaint)
                        );
                    }

                    return true;
                },
            })
        );
    }

    completeMove() {
        this.context.actions.push(
            new BattleAction({
                type: ActionType.CODE,
                description: 'Attack completed.',
                action: () => {
                    if (this.context.completedHits > 1) {
                        this.actions.unshift(
                            this.ag.readText(
                                `Hit ${this.context.completedHits} time${
                                    this.context.completedHits === 1 ? '' : 's'
                                }.`,
                                { delay: 50 }
                            )
                        );
                    }
                    return true;
                },
            })
        );
    }

    readMoveEventTexts() {
        this.context.moveEventTexts.forEach((moveEventText) => {
            this.context.actions.push(this.ag.readText(moveEventText, { delay: 50 }));
        });
    }

    preventAttackerMoveDueToFlinch() {
        if (this.context.attacker.isFlinched()) {
            this.context.actions.push(
                this.ag.readText(`${this.context.attacker.name} flinched!`, { delay: 50 })
            );
            // Clear flinch immediately after it prevents the move
            this.context.actions.push(
                this.ag.script(() => {
                    this.context.attacker.volatileStatuses.flinch = 0;
                })
            );
            return true;
        }
        return false;
    }

    preventAttackerMoveDueToProtect() {
        if (this.context.defender.isProtected()) {
            this.context.actions.push(
                this.ag.readText(`${this.context.defender.name} protected itself!`, { delay: 50 })
            );
            return true;
        }
        return false;
    }

    shouldPreventAttackDueToFailure() {
        if (this.context.move.category === 'status') {
            if (!this.context.shouldVolatileStatusEffectApplicationSucceed) {
                this.context.actions.push(this.ag.readText('But it failed!', { delay: 50 }));
                return true;
            }
        }
        return false;
    }

    /**
     * Returns the move the player will use for the given slot index (0-3).
     * If the options menu has a Move Slot cheat set for that slot (not 'off'), that move is used instead.
     */
    getPlayerMoveForSlot(slotIndex) {
        let move = this.playerPokemon.moves[slotIndex] ?? null;
        const optionsMenu = getOSM()?.optionsMenu;
        if (optionsMenu && slotIndex >= 0 && slotIndex <= 3) {
            const overrides = [
                optionsMenu.battleCheatMoveSlot0,
                optionsMenu.battleCheatMoveSlot1,
                optionsMenu.battleCheatMoveSlot2,
                optionsMenu.battleCheatMoveSlot3,
            ];
            const overrideId = overrides[slotIndex];
            if (overrideId && overrideId !== 'off') {
                try {
                    move = Move.getById(overrideId);
                } catch (_) {
                    // Keep original move if override id is invalid
                }
            }
        }
        return move;
    }

    setUpBattleActions(move, attacker, defender) {
        // Check for custom move implementation
        if (move.identifier && customMoveImplementations.has(move.identifier)) {
            const CustomMoveClass = customMoveImplementations.get(move.identifier);
            if (CustomMoveClass && typeof CustomMoveClass.setUpBattleActions === 'function') {
                return CustomMoveClass.setUpBattleActions(this, move, attacker, defender);
            }
        }

        // Default implementation
        this.setUpBattleActionsContext(move, attacker, defender);

        if (this.context.attackerFainted) return this.context.actions;

        if (this.preventAttackerMoveDueToFlinch()) return this.context.actions;

        this.removeAttackerStatus();

        if (this.preventAttackerMoveDueToStatus()) return this.context.actions;

        this.announceMoveUsage();
        this.reduceMovePP();

        if (this.preventAttackerMoveDueToProtect()) return this.context.actions;

        this.calculateNumberOfHits();
        if (!this.shouldMoveHitTarget()) return this.context.actions;

        if (!this.shouldMoveAffect()) {
            // Add effectiveness text to actions array
            this.context.actions.push(
                this.ag.readText(this.effectivenessBattleText(this.context.move, this.context.defender), {
                    delay: 50,
                })
            );
            return this.context.actions;
        }

        this.calculateVolatileStatusEffectApplicationSuccess();
        if (this.shouldPreventAttackDueToFailure()) return this.context.actions;

        for (let i = 0; i < this.context.numHits; i++) {
            this.calculateDamageDealt();
            this.animateAttack();
            this.dealDamage();
            this.clearActionsIfDefenderFainted();
            this.applyStatModifiers();
            this.applyVolatileStatusEffect();
            this.applyNonVolatileStatusEffect();
            this.applyEntryHazards();
            this.recoverHP();
            this.dealRecoilDamage();
            this.clearActionsIfDefenderFainted();
        }
        this.completeMove();
        this.readMoveEventTexts();
        this.performMoveAfterEffects();
        return this.context.actions;
    }

    performMoveAfterEffects() {
        // Any effects that occur dure to a volatile status effect at the end of the move
        if (this.context.defender.isForcedOut()) {
            if (this.context.defender === this.playerPokemon) {
                this.stateQueue = this.stateQueue.filter((s) => s !== BATTLE_SEQUENCE_PLAYER_MOVE);
            } else {
                this.stateQueue = this.stateQueue.filter((s) => s !== BATTLE_SEQUENCE_FOE_MOVE);
            }
        }
    }

    calculateFoeMove(attacker) {
        const attackerMoves = [];
        if (attacker.moves[0]) {
            attackerMoves.push(attacker.moves[0]);
        }
        if (attacker.moves[1]) {
            attackerMoves.push(attacker.moves[1]);
        }
        if (attacker.moves[2]) {
            attackerMoves.push(attacker.moves[2]);
        }
        if (attacker.moves[3]) {
            attackerMoves.push(attacker.moves[3]);
        }

        // Wild encounter cheat: restrict foe to moves of a specific category; use Struggle if none match
        let pool = attackerMoves;
        if (attacker.isWild?.()) {
            const optionsMenu = getOSM()?.optionsMenu;
            const category = optionsMenu?.overworldCheatFoeMoveCategory;
            if (category && category !== 'any') {
                const filtered = attackerMoves.filter((m) => m && m.category === category);
                if (filtered.length > 0) {
                    pool = filtered;
                } else {
                    this._foeHasNoValidMoves = true;
                    return Move.struggle();
                }
            }
        }

        if (pool.length === 0) {
            this._foeHasNoValidMoves = true;
            return Move.struggle();
        }
        let chosenMove;
        while (!chosenMove) {
            const potentialMove = pool[Math.floor(Math.random() * pool.length)];
            if (potentialMove) {
                chosenMove = potentialMove;
            }
        }
        return chosenMove;
    }

    calculatePokemonSpeed(pokemon) {
        const paralysisModifier = pokemon.isParalyzed() ? 0.5 : 1;
        return Math.round(
            pokemon.stats.speed * paralysisModifier * stageMultiplier.general[pokemon.battleStages.speed]
        );
    }

    moveOrder(pokemon1, pokemon2, pokemon1Move, pokemon2Move) {
        if (pokemon1Move === null || pokemon1Move === undefined) {
            return [pokemon2];
        }
        if (pokemon1Move.priority > pokemon2Move.priority) {
            return [pokemon1, pokemon2];
        }

        if (pokemon1Move.priority < pokemon2Move.priority) {
            return [pokemon2, pokemon1];
        }

        const pokemon1Speed = this.calculatePokemonSpeed(pokemon1);
        const pokemon2Speed = this.calculatePokemonSpeed(pokemon2);

        if (pokemon1Speed > pokemon2Speed) {
            return [pokemon1, pokemon2];
        } else if (pokemon2Speed > pokemon1Speed) {
            return [pokemon2, pokemon1];
        } else {
            return Math.random() > 0.5 ? [pokemon1, pokemon2] : [pokemon2, pokemon1];
        }
    }

    calculateMoveHits(move, attacker, defender) {
        // Check for battle cheats: Player/Opponent Move Never Misses and Always Misses
        const optionsMenu = getOSM()?.optionsMenu;
        const isPlayerAttacking = attacker === this.playerPokemon;
        const isFoeAttacking = attacker === this.foePokemon;

        // Player move never misses cheat
        if (isPlayerAttacking && optionsMenu?.battleCheatPlayerMoveNeverMisses === 'on') {
            return true;
        }

        // Player move always misses cheat
        if (isPlayerAttacking && optionsMenu?.battleCheatPlayerMoveAlwaysMisses === 'on') {
            return false;
        }

        // Opponent move never misses cheat
        if (isFoeAttacking && optionsMenu?.battleCheatOpponentMoveNeverMisses === 'on') {
            return true;
        }

        // Opponent move always misses cheat
        if (isFoeAttacking && optionsMenu?.battleCheatOpponentMoveAlwaysMisses === 'on') {
            return false;
        }

        // One-Hit KO Moves
        const accuracy = move.getAccuracy(attacker, defender, this);
        if (move.oneHitKO) {
            if (defender.level > attacker.level) {
                return false;
            }

            return accuracy + (attacker.level - defender.level) > Math.round(Math.random() * 100);
        }

        if (defender.getIdentifiedBy()?.identifier === attacker.identifier) {
            return true;
        }

        // Regular Moves
        if (accuracy === 0 || accuracy === -1) {
            return true;
        }

        if (move.isStatus() && accuracy === undefined) {
            return true;
        }

        const modifier =
            stageMultiplier.accuracy[
                Math.max(attacker.battleStages.accuracy - defender.battleStages.evasiveness, -6)
            ];

        let abilityModifier = 1;
        if (attacker.ability) {
            switch (attacker.ability.identifier) {
                case 'compound_eyes':
                    abilityModifier = 1.3;
                    break;
                case 'hustle':
                    // Hustle lowers accuracy of physical moves by 20%
                    // One-hit KO moves and special moves are not affected
                    // (One-hit KO moves return early, so we only need to check if it's physical)
                    if (move.isPhysical()) {
                        abilityModifier = 0.8;
                    }
                    break;
            }
        }
        return accuracy * modifier * abilityModifier >= Math.round(Math.random() * 100);
    }

    shouldMoveAffect() {
        return this.calculateEffectiveness(this.context.move, this.context.defender) > 0;
    }

    calculateEffectiveness(move, pokemon) {
        if (move.type === 'typeless') {
            return 1;
        }
        let idd = pokemon.getIdentifiedBy()?.identifier === this.context.attacker.identifier;
        if (
            idd &&
            (typeEffectiveness[move.type][pokemon.type1] === 0 ||
                typeEffectiveness[move.type][pokemon.type2] === 0)
        ) {
            return 1;
        }

        // this is defaulting to 1 when the value is 0. It should only default to 1 if the value is undefined or null.
        let effect1 = typeEffectiveness[move.type][pokemon.type1];
        let effect2 = typeEffectiveness[move.type][pokemon.type2];

        if (effect1 === undefined || effect1 === null) {
            effect1 = 1;
        }
        if (effect2 === undefined || effect2 === null) {
            effect2 = 1;
        }

        // If both types are the same, don't double count the effect
        if (pokemon.type1 === pokemon.type2) {
            return effect1;
        }

        return effect1 * effect2;
    }

    effectivenessBattleText(move, pokemon) {
        const effectiveness = this.calculateEffectiveness(move, pokemon);
        switch (effectiveness) {
            case 0:
                return `It doesn't affect ${pokemon.name}`;
            case 0.25:
                return `Not very effective...`;
            case 0.5:
                return `Not very effective...`;
            case 1:
                return '';
            case 2:
                return `It's super effective!`;
            case 4:
                return `It's super effective!`;
        }
        return '';
    }

    setUpEndTurnContext(playerPokemon, foePokemon) {
        this.endTurnContext = {
            playerPokemon: playerPokemon,
            foePokemon: foePokemon,
            actions: [],
        };
    }

    setUpEndTurnActions(playerPokemon, foePokemon) {
        this.setUpEndTurnContext();
        const participants = [playerPokemon, foePokemon];
        participants.forEach(() => {});
    }

    updateHP(pokemon, hpDelta) {
        return new BattleAction({
            type: ActionType.ANIMATION,
            description: 'Animate health bar update',
            animationFunction: () => {
                if (hpDelta < 0) {
                    // Decreasing HP
                    pokemon.stats.hp -= 0.5;
                    hpDelta += 0.5;
                } else if (hpDelta > 0) {
                    // Increasing HP
                    if (pokemon.stats.hp < pokemon.stats.maxHp) {
                        pokemon.stats.hp += 0.5;
                    }
                    hpDelta -= 0.5;
                }
                return hpDelta === 0 || pokemon.stats.hp === 0 || pokemon.stats.hp === pokemon.stats.maxHp;
            },
        });
    }

    setupActionsForCurrentState(options = {}) {
        const d = this.battleGUI.drawables;
        console.log(`-- Starting State: ${this.currentState} --`);
        this.stateHistory.unshift(this.currentState);
        switch (this.currentState) {
            case TRAINER_BATTLE_INTRO:
                this.actions = [
                    this.ag.script(() => {
                        this.foeTrainer.party.forEach((pokemon) => {
                            pokemon.loadBattleCry();
                        });
                    }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description:
                            'Trainer Battle intro animation. Foe comes in from the left, player comes in from the right.',
                        startAction: () => {
                            d.foeTrainer.position.x = -96;
                            d.foePokemon.visible = false;
                            d.foePokemon.position.x = 144;
                            d.foePokemon.position.y = 8;
                            d.foePartySummary.setParty(this.foeTrainer.party);
                        },
                        animationFunction: () => {
                            this.battleGUI.undarken();
                            d.foeTrainer.position.x += ANIMATION_SPEED;
                            d.player.position.x -= ANIMATION_SPEED;
                            d.foeBackground.position.x += ANIMATION_SPEED;
                            d.playerBackground.position.x -= ANIMATION_SPEED;
                            return d.foeBackground.position.x === 0;
                        },
                        battleStateMachine: this,
                    }),
                ];
                break;
            case WILD_BATTLE_INTRO:
                this.actions = [
                    this.ag.script(() => {
                        this.foePokemon.loadBattleCry();
                        d.foePokemon.position = {
                            x: -32 - 64,
                            y: 16,
                        };
                        d.foePokemon.visible = true;
                        d.foePokemon.scale = 1;
                    }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description:
                            'Wild Pokemon Battle intro animation. Foe comes in from the left, player comes in from the right.',
                        animationFunction: () => {
                            d.foePokemon.position.x += ANIMATION_SPEED;
                            d.player.position.x -= ANIMATION_SPEED;
                            d.foeBackground.position.x += ANIMATION_SPEED;
                            d.playerBackground.position.x -= ANIMATION_SPEED;
                            d.foePokemon.darken(0.6);
                            this.battleGUI.undarken();
                            return d.foeBackground.position.x === 0;
                        },
                        battleStateMachine: this,
                    }),
                    this.ag.playBattleCry(this.foePokemon),
                    this.ag.script(() => {
                        this.player.pokedex.addSeenPokemon(this.foePokemon);
                    }),
                    this.ag.resetAssetDarkness({ asset: d.foePokemon }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate foe information box into the screen from the left',
                        animationFunction: () => {
                            d.foeInfo.position.x += 2 * ANIMATION_SPEED;
                            return d.foeInfo.position.x === 0;
                        },
                        battleStateMachine: this,
                    }),
                    this.ag.readText(`A wild ${this.foePokemon.name} appeared!`),
                    this.ag.finishReading(),
                ];
                break;
            case TRAINER_WOULD_LIKE_TO_BATTLE:
                this.actions = [
                    this.parallel(
                        this.series([
                            this.ag.script(() => {
                                // Foe
                                d.foePartySummary.position.x = -240;
                                d.foePartySummary.pokeballs[0].position.x = -28;
                                d.foePartySummary.pokeballs[0].position.y = d.foePartySummary.position.y;
                                d.foePartySummary.pokeballs[0].debugMode = true;

                                d.foePartySummary.pokeballs[1].position.x = -38;
                                d.foePartySummary.pokeballs[1].position.y = d.foePartySummary.position.y;

                                d.foePartySummary.pokeballs[2].position.x = -48;
                                d.foePartySummary.pokeballs[2].position.y = d.foePartySummary.position.y;

                                d.foePartySummary.pokeballs[3].position.x = -58;
                                d.foePartySummary.pokeballs[3].position.y = d.foePartySummary.position.y;

                                d.foePartySummary.pokeballs[4].position.x = -68;
                                d.foePartySummary.pokeballs[4].position.y = d.foePartySummary.position.y;

                                d.foePartySummary.pokeballs[5].position.x = -78;
                                d.foePartySummary.pokeballs[5].position.y = d.foePartySummary.position.y;
                            }),

                            this.ag.showUIElement(d.foePartySummary),
                            this.ag.moveUIElement(d.foePartySummary, 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[0], 104, 0, 8),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[1], 104, 0, 8),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[2], 104, 0, 8),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[3], 104, 0, 8),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[4], 104, 0, 8),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[5], 104, 0, 8),
                        ]),
                        this.series([
                            this.ag.script(() => {
                                // Player
                                d.playerPartySummary.position.x = 240;
                                d.playerPartySummary.pokeballs[0].position.x = 260;
                                d.playerPartySummary.pokeballs[0].position.y =
                                    d.playerPartySummary.position.y;

                                d.playerPartySummary.pokeballs[1].position.x = 270;
                                d.playerPartySummary.pokeballs[1].position.y =
                                    d.playerPartySummary.position.y;

                                d.playerPartySummary.pokeballs[2].position.x = 280;
                                d.playerPartySummary.pokeballs[2].position.y =
                                    d.playerPartySummary.position.y;

                                d.playerPartySummary.pokeballs[3].position.x = 290;
                                d.playerPartySummary.pokeballs[3].position.y =
                                    d.playerPartySummary.position.y;

                                d.playerPartySummary.pokeballs[4].position.x = 300;
                                d.playerPartySummary.pokeballs[4].position.y =
                                    d.playerPartySummary.position.y;

                                d.playerPartySummary.pokeballs[5].position.x = 310;
                                d.playerPartySummary.pokeballs[5].position.y =
                                    d.playerPartySummary.position.y;
                            }),
                            this.ag.showUIElement(d.playerPartySummary),
                            this.ag.moveUIElement(d.playerPartySummary, -104, 0, 2),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[0], -104, 0, 8),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[1], -104, 0, 8),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[2], -104, 0, 8),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[3], -104, 0, 8),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[4], -104, 0, 8),
                            this.ag.moveUIElement(d.playerPartySummary.pokeballs[5], -104, 0, 8),
                        ]),
                        this.ag.readText(`${this.foeTrainer.name} would like to battle!`)
                    ),
                    this.ag.finishReading(),
                ];
                break;
            case TRAINER_SWITCHES_POKEMON:
                this.actions = [
                    this.series([
                        this.ag.script(() => {
                            if (this.foePokemon.stats.hp > 0) {
                                this.foePokemonQueue.push(this.foePokemon);
                            }
                            this.foePokemon = this.foePokemonQueue[0];
                            this.player.pokedex.addSeenPokemon(this.foePokemon);
                            d.foePokemon.visible = false;
                        }),
                        this.parallel([
                            this.ag.moveUIElement(d.foePartySummary, 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[0], 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[1], 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[2], 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[3], 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[4], 104, 0, 2),
                            this.ag.moveUIElement(d.foePartySummary.pokeballs[5], 104, 0, 2),
                        ]),
                        this.ag.delay(90),
                    ]),
                ];
                break;
            case TRAINER_POKEMON_FORCED_OUT: {
                const availableFoePokemon = [];
                this.foePokemonQueue.forEach((p) => {
                    if (!p.isFainted() && p !== this.foePokemon) {
                        availableFoePokemon.push(p);
                    }
                });
                this.upcomingPokemon = availableFoePokemon[0];
                this.actions.push(this.ag.hideUIElement(d.foeInfo));
                this.actions.push(
                    this.ag.script(() => {
                        // Clear identifiedBy for both pokemon when switching
                        this.foePokemon.identifiedBy = null;
                        this.playerPokemon.identifiedBy = null;
                        this.clearFieldEffectsForSource(this.foePokemon);
                        this.foePokemon.resetVolatileStatuses();
                        this.foePokemon = this.upcomingPokemon;
                        // Clear identifiedBy for the new pokemon as well
                        this.foePokemon.identifiedBy = null;
                    })
                );
                this.actions.push(this.ag.hideUIElement(d.foePokemon));
                this.actions.push(...this.ag.sendOutFoePokemon(this.upcomingPokemon));
                break;
            }

            case PLAYER_POKEMON_FORCED_OUT: {
                const availablePokemon = [];
                this.player.party.forEach((p) => {
                    if (!p.isFainted() && p !== this.playerPokemon) {
                        availablePokemon.push(p);
                    }
                });
                this.upcomingPokemon = availablePokemon[0];

                this.actions.push(this.ag.hideUIElement(d.playerInfo));
                this.actions.push(
                    this.ag.script(() => {
                        // Clear identifiedBy for both pokemon when switching
                        this.playerPokemon.identifiedBy = null;
                        this.foePokemon.identifiedBy = null;
                        this.clearFieldEffectsForSource(this.playerPokemon);
                        this.playerPokemon.resetVolatileStatuses();
                        this.playerPokemon = this.upcomingPokemon;
                        // Clear identifiedBy for the new pokemon as well
                        this.playerPokemon.identifiedBy = null;
                    })
                );
                this.actions.push(this.ag.hideUIElement(d.playerPokemon));
                this.actions.push(...this.ag.sendOutPlayerPokemon(this.upcomingPokemon));

                break;
            }

            case FOE_POKEMON_SENT_OUT_END:
                this.actions.push(
                    this.ag.readText(`${this.foePokemon.name} was dragged out!`, { delay: 50 })
                );
                break;

            case PLAYER_POKEMON_SENT_OUT_END:
                this.actions.push(
                    this.ag.readText(`${this.playerPokemon.name} was dragged out!`, { delay: 50 })
                );
                break;
            case TRAINER_SENDS_OUT_POKEMON:
                this.actions = [
                    this.ag.readText(`${this.foeTrainer.name} sent out ${this.foePokemon.name}!`),
                    this.ag.hideUIElement(d.foePokemon),
                    this.parallel([
                        this.ag.moveUIElement(d.foePartySummary, 240, 0, 2),
                        this.parallel([
                            this.ag.fadeOutUIElement(d.foePartySummary),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[0]),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[1]),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[2]),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[3]),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[4]),
                            this.ag.fadeOutUIElement(d.foePartySummary.pokeballs[5]),
                        ]),
                        this.parallel([
                            this.series([
                                this.ag.delay(10),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[0], 240, 0, 8),
                            ]),
                            this.series([
                                this.ag.delay(10),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[1], 240, 0, 8),
                            ]),
                            this.series([
                                this.ag.delay(20),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[2], 240, 0, 8),
                            ]),
                            this.series([
                                this.ag.delay(30),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[3], 240, 0, 8),
                            ]),
                            this.series([
                                this.ag.delay(40),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[4], 240, 0, 8),
                            ]),
                            this.series([
                                this.ag.delay(50),
                                this.ag.moveUIElement(d.foePartySummary.pokeballs[5], 240, 0, 8),
                            ]),
                        ]),
                        this.series([...this.ag.sendOutFoePokemon(this.foePokemon)]),
                    ]),
                    this.ag.script(() => {
                        // Foe
                        d.foePartySummary.position.x = -240;
                        d.foePartySummary.pokeballs[0].position.x = -28;
                        d.foePartySummary.pokeballs[0].position.y = d.foePartySummary.position.y;
                        d.foePartySummary.pokeballs[0].debugMode = true;

                        d.foePartySummary.pokeballs[1].position.x = -38;
                        d.foePartySummary.pokeballs[1].position.y = d.foePartySummary.position.y;

                        d.foePartySummary.pokeballs[2].position.x = -48;
                        d.foePartySummary.pokeballs[2].position.y = d.foePartySummary.position.y;

                        d.foePartySummary.pokeballs[3].position.x = -58;
                        d.foePartySummary.pokeballs[3].position.y = d.foePartySummary.position.y;

                        d.foePartySummary.pokeballs[4].position.x = -68;
                        d.foePartySummary.pokeballs[4].position.y = d.foePartySummary.position.y;

                        d.foePartySummary.pokeballs[5].position.x = -78;
                        d.foePartySummary.pokeballs[5].position.y = d.foePartySummary.position.y;
                    }),
                    this.parallel([
                        this.ag.fadeInUIElement(d.foePartySummary, 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[0], 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[1], 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[2], 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[3], 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[4], 1),
                        this.ag.fadeInUIElement(d.foePartySummary.pokeballs[5], 1),
                    ]),
                ];
                break;
            case PLAYER_SENDS_OUT_POKEMON:
                if (this.isTrainerBattle()) {
                    this.actions = [
                        this.ag.readText(`Go ${this.playerPokemon.nickname || this.playerPokemon.name}!`, {
                            showContinueArrow: false,
                        }),
                        this.ag.script(() => {
                            this.playerPokemon.loadBattleCry();
                        }),
                        new BattleAction({
                            type: ActionType.ANIMATION,
                            description: 'Animate player party summary',
                            startAction: () => {
                                d.playerPartySummary.visible = true;
                            },
                            animationFunction: () => {
                                d.playerPartySummary.position.x -= 2 * ANIMATION_SPEED;
                                return d.playerPartySummary.position.x <= 136;
                            },
                        }),
                        this.parallel(
                            this.ag.moveUIElement(d.playerPartySummary, -240, 0, 2),
                            this.parallel([
                                this.ag.fadeOutUIElement(d.playerPartySummary),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[0]),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[1]),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[2]),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[3]),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[4]),
                                this.ag.fadeOutUIElement(d.playerPartySummary.pokeballs[5]),
                            ]),
                            this.parallel([
                                this.series([
                                    this.ag.delay(10),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[0], -240, 0, 8),
                                ]),
                                this.series([
                                    this.ag.delay(20),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[1], -240, 0, 8),
                                ]),
                                this.series([
                                    this.ag.delay(30),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[2], -240, 0, 8),
                                ]),
                                this.series([
                                    this.ag.delay(40),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[3], -240, 0, 8),
                                ]),
                                this.series([
                                    this.ag.delay(50),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[4], -240, 0, 8),
                                ]),
                                this.series([
                                    this.ag.delay(60),
                                    this.ag.moveUIElement(d.playerPartySummary.pokeballs[5], -240, 0, 8),
                                ]),
                            ]),
                            this.ag.animatePlayerTrainerThrowingPokeball(this.playerPokemon)
                        ),
                        [
                            this.ag.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
                            ...this.ag.sendOutPlayerPokemon(this.playerPokemon).slice(3), // Skip loadBattleCry, pokeball throw animation, and spawn audio since we're using the trainer animation
                        ],
                    ];
                } else {
                    this.actions = [
                        this.ag.readText(`Go ${this.playerPokemon.nickname || this.playerPokemon.name}!`, {
                            showContinueArrow: false,
                        }),
                        this.ag.script(() => {
                            this.playerPokemon.loadBattleCry();
                        }),
                        this.ag.animatePlayerTrainerThrowingPokeball(this.playerPokemon),
                        this.ag.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
                        ...this.ag.sendOutPlayerPokemon(this.playerPokemon).slice(3),
                    ];
                }
                break;
            case CHOOSE_ACTION:
                this.actions = [
                    this.ag.script(() => {
                        d.foePokemon.position = {
                            x: 144,
                            y: 16,
                        };
                    }),
                    this.ag.script(() => {
                        this.battleGUI.text.visible = true;
                    }),
                    this.ag.clearText(),
                    this.ag.script(() => this.battleGUI.text.resetScrolling()),
                    this.ag.instantText(`What will\n${this.playerPokemon.name} do?`, {
                        showContinueArrow: false,
                    }),
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a battle option',
                        battleStateMachine: this,
                        startAction: () => {
                            d.pokemonMenu.sprites.pokemonOptionsBox.visible = false;
                            d.pokemonMenu.visible = false;
                            d.battleMenu.visible = true;
                            d.battleText = '';
                        },
                        action: () => {
                            d.playerPokemon.upDown();
                            d.playerInfo.upDown();
                            this.battleOption = this.awaitOptionInput(this.battleOption).currentOption;
                            return this.awaitOptionInput(this.battleOption).selected;
                        },
                        endAction: () => {
                            d.playerPokemon.stopUpDown();
                            d.playerInfo.stopUpDown();
                            this.battleGUI.text.visible = false;
                            d.battleMenu.visible = false;
                        },
                    }),
                ];
                break;
            case CHOOSE_MOVE:
                this.actions = [
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a move',
                        battleStateMachine: this,
                        debounce: 15,
                        startAction: () => {
                            d.moveMenu.visible = true;
                            d.battleMenu.visible = false;
                            this.battleText = '';
                        },
                        action: () => {
                            d.playerPokemon.upDown();
                            d.playerInfo.upDown();
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            this.moveOption = this.awaitOptionInput(this.moveOption).currentOption;
                            return (
                                this.awaitOptionInput(this.moveOption).selected &&
                                this.getPlayerMoveForSlot(this.moveOption) !== null
                            );
                        },
                        endAction: () => {
                            d.moveMenu.visible = false;
                            d.playerPokemon.stopUpDown();
                            d.playerInfo.stopUpDown();
                            this.battleGUI.text.visible = true;
                        },
                    }),
                ];
                break;
            case NO_PP_LEFT_FOR_THIS_MOVE:
                this.actions = [];
                this.actions.push(
                    this.ag.hideUIElement(d.moveMenu),
                    this.ag.readText('There is no PP left for this move!'),
                    this.ag.finishReading()
                );
                break;
            case PRE_BATTLE_SEQUENCE:
                this.actions = [];
                break;
            case BATTLE_SEQUENCE_PLAYER_MOVE: {
                this.actions = [];
                const playerMove = this.getPlayerMoveForSlot(this.moveOption);
                this.actions = this.setUpBattleActions(playerMove, this.playerPokemon, this.foePokemon);

                break;
            }
            case BATTLE_SEQUENCE_FOE_MOVE: {
                const foeMove = this.foeMove;
                this.actions = this.setUpBattleActions(foeMove, this.foePokemon, this.playerPokemon);
                if (this._foeHasNoValidMoves) {
                    this.actions.unshift(
                        this.ag.readText(`${this.foePokemon.name} doesn't know any moves!`),
                        this.ag.finishReading()
                    );
                    this._foeHasNoValidMoves = false;
                }
                break;
            }
            case END_BATTLE_SEQUENCE: {
                this.setUpEndTurnContext(this.playerPokemon, this.foePokemon);
                const p1 = this.endTurnContext.playerPokemon;
                const p2 = this.endTurnContext.foePokemon;
                const ctx = this.endTurnContext;

                const participants = [p1, p2].sort((a, b) => a.stats.speed > b.stats.speed);

                // Whirlwind or Roar
                if (p2.isForcedOut()) {
                    if (this.foeTrainer === null) {
                        // In a wild battle, just end it
                        this.stateQueue.push(END_BATTLE);
                    } else {
                        this.stateQueue.push(TRAINER_POKEMON_FORCED_OUT);
                        this.stateQueue = this.stateQueue.filter((s) => s !== BATTLE_SEQUENCE_FOE_MOVE);
                    }
                    return;
                }

                if (p1.isForcedOut()) {
                    if (this.foeTrainer === null) {
                        // In a wild battle, just end it
                        this.stateQueue.push(END_BATTLE);
                    } else {
                        this.stateQueue.push(PLAYER_POKEMON_FORCED_OUT);
                        this.stateQueue = this.stateQueue.filter((s) => s !== BATTLE_SEQUENCE_PLAYER_MOVE);
                    }
                    return;
                }

                participants.forEach((p) => {
                    const foe = p === p1 ? p2 : p1;
                    if (p.stats.hp === 0) {
                        console.log(`${p.name} fainted. skipping end battle sequence.`);
                        return;
                    }
                    if (p.status1) {
                        ctx.actions.push(this.ag.readText(p.status1.endTurnEffectText, { delay: 50 }));
                        ctx.actions.push(this.updateHP(p, p.getDamageDealtByPrimaryStatus()));
                        ctx.actions.push(this.ag.heldBerryConsumptionIfTriggeredAction(p));
                        ctx.actions.push(
                            this.ag.script(() => {
                                p.status1.currentTurn += 1;
                            })
                        );
                    }

                    // Recover health due to ingrain
                    if (p.isRooted() && !p.isHPFull()) {
                        ctx.actions.push(this.ag.readText(`${p.name} absorbed nutrients with its roots!`));
                        ctx.actions.push(this.ag.delay(50));
                        ctx.actions.push(this.updateHP(p, p.getHPRecoveredByVolatileStatus('rooted')));
                    }

                    if (p.isAquaRinged() && !p.isHPFull()) {
                        ctx.actions.push(this.ag.readText(`Aqua Ring restored ${p.name}'s HP!`));
                        ctx.actions.push(this.ag.delay(50));
                        ctx.actions.push(this.updateHP(p, p.getHPRecoveredByVolatileStatus('aqua_ring')));
                    }

                    if (p.isSeeded()) {
                        ctx.actions.push(
                            this.ag.readText(`${p.name}'s health nutrients was sapped by Leech Seed!`)
                        );
                        ctx.actions.push(this.ag.delay(50));
                        // Reduce health by 1/16 to seeded pokemon
                        ctx.actions.push(this.updateHP(p, p.getDamageDealtByVolatileStatus('seeded')));

                        ctx.actions.push(this.ag.delay(50));

                        // Increase health by same amount to other pokemon
                        ctx.actions.push(this.updateHP(foe, p.getDamageDealtByVolatileStatus('seeded') * -1));
                    }
                });

                // Update volatile status turns for both pokemon at end of turn
                this.endTurnContext.actions.push(
                    this.ag.script(() => {
                        p1.updateVolatileStatusTurns();
                        p2.updateVolatileStatusTurns();
                    })
                );

                this.endTurnContext.actions.push(
                    this.ag.script(() => {
                        this.currentTurn += 1;
                    })
                );

                this.actions = this.endTurnContext.actions;

                break;
            }
            case FOE_FAINTS:
                this.actions = [
                    this.ag.playBattleCry(this.foePokemon),
                    this.ag.delay(60),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate foe fainting',
                        animationFunction: () => {
                            this.foePokemon.frontSprite.verticalOffset += 2;
                            this.foePokemon.frontSprite.verticalCrop += 2;
                            return this.foePokemon.frontSprite.verticalOffset === 128;
                        },
                        endAction: () => {
                            d.foeInfo.visible = false;
                            // Clear identifiedBy when pokemon faints
                            this.foePokemon.identifiedBy = null;
                        },
                    }),
                    this.ag.readText(`Foe ${this.foePokemon.name} fainted.`),
                    this.ag.script(() => {
                        this.clearFieldEffectsForSource(this.foePokemon);
                        this.foePokemonQueue.shift();
                    }),
                    this.ag.finishReading(),
                ];
                break;
            case SETUP_EXPERIENCE_GAIN_QUEUE:
                // TODO: Include pokemon with EXP share
                this.participantPokemon = new Set(
                    [...this.participantPokemon].filter((pokemon) => pokemon.stats.hp > 0)
                );
                this.participantPokemon.forEach((pokemon) => {
                    pokemon.updateEVs(this.foePokemon);
                    this.experienceGainQueue.push({
                        pokemon: pokemon,
                        experienceGained: Math.ceil(
                            this.foePokemon.experienceYield / this.participantPokemon.size
                        ), // TODO: Move this logic into a method in the Pokemon class.
                    });
                });
                this.experienceGainQueue.sort((a, b) => {
                    if (a.pokemon === this.playerPokemon) {
                        return -1; // a should come before b
                    } else if (b.pokemon === this.playerPokemon) {
                        return 1; // b should come before a
                    } else {
                        return 0; // no change in order
                    }
                });
                break;
            case GAIN_EXPERIENCE_NOTIFICATION:
                // Displays the text saying how much Exp the current pokemon gained.
                this.actions = [
                    this.ag.readText(
                        `${this.experienceGainQueue[0].pokemon.name} gained ${this.experienceGainQueue[0].experienceGained} experience points!`
                    ),
                    this.ag.finishReading(),
                ];
                break;
            case GAIN_EXPERIENCE_ACTIVE_POKEMON:
                this.actions = [
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate experience increase',
                        animationFunction: () => {
                            // TODO: Make exp gain instantaneous for non active participants
                            const expGainSpeed = Math.round(
                                (this.experienceGainQueue[0].pokemon.getNextLevelExperience() -
                                    this.experienceGainQueue[0].pokemon.getCurrentLevelExperience()) /
                                    100
                            );
                            const expToNextLevel =
                                this.experienceGainQueue[0].pokemon.getNextLevelExperience() -
                                this.experienceGainQueue[0].pokemon.experience;
                            const remainingExp = this.experienceGainQueue[0].experienceGained;
                            this.experienceGainQueue[0].pokemon.experience += Math.max(
                                1,
                                Math.min(expGainSpeed, remainingExp, expToNextLevel)
                            );
                            this.experienceGainQueue[0].experienceGained -= Math.max(
                                1,
                                Math.min(expGainSpeed, remainingExp, expToNextLevel)
                            );
                            return (
                                this.experienceGainQueue[0].pokemon.experience ===
                                    this.experienceGainQueue[0].pokemon.getNextLevelExperience() ||
                                this.experienceGainQueue[0].experienceGained === 0
                            );
                        },
                    }),
                ];
                break;

            case LEVEL_UP_POKEMON:
                this.experienceGainQueue[0].pokemon.levelUp();
                this.actions = [
                    this.ag.readText(
                        `${this.experienceGainQueue[0].pokemon.name} grew to level ${this.experienceGainQueue[0].pokemon.level}!`
                    ),
                    this.ag.finishReading(),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Show stat increase box',
                        startAction: () => {},
                        animationFunction: () => {
                            // TODO: Also show banner for inactive pokemon if inactive pokemon leveled up.
                            d.statBox.visible = true;
                            const increaseHistory =
                                this.experienceGainQueue[0].pokemon.statIncreaseHistory[
                                    this.experienceGainQueue[0].pokemon.level
                                ].increase;
                            d.statBox.hpStat.text = `+ ${increaseHistory.hp}`;
                            d.statBox.attackStat.text = `+ ${increaseHistory.attack}`;
                            d.statBox.defenseStat.text = `+ ${increaseHistory.defense}`;
                            d.statBox.specialAttackStat.text = `+ ${increaseHistory.specialAttack}`;
                            d.statBox.specialDefenseStat.text = `+ ${increaseHistory.specialDefense}`;
                            d.statBox.speedStat.text = `+ ${increaseHistory.speed}`;
                            return true;
                        },
                    }),
                    this.ag.finishReading({ debounce: 15 }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Show stat increase box',
                        startAction: () => {},
                        animationFunction: () => {
                            d.statBox.visible = true;
                            const statTotal =
                                this.experienceGainQueue[0].pokemon.statIncreaseHistory[
                                    this.experienceGainQueue[0].pokemon.level
                                ].total;
                            d.statBox.hpStat.text = String(statTotal.hp);
                            d.statBox.attackStat.text = String(statTotal.attack);
                            d.statBox.defenseStat.text = String(statTotal.defense);
                            d.statBox.specialAttackStat.text = String(statTotal.specialAttack);
                            d.statBox.specialDefenseStat.text = String(statTotal.specialDefense);
                            d.statBox.speedStat.text = String(statTotal.speed);
                            return true;
                        },
                    }),
                    this.ag.finishReading({ debounce: 15 }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        animationFunction: () => {
                            d.statBox.visible = false;
                            return true;
                        },
                    }),
                ];
                break;

            case NEW_MOVE_AVAILABLE: {
                const curPokemon = this.experienceGainQueue[0].pokemon;
                if (curPokemon.newMoves.length === 0) {
                    curPokemon.moveSet[curPokemon.level].forEach((moveId) => {
                        curPokemon.newMoves.push(Move.getById(moveId));
                    });
                }
                break;
            }

            case CURRENT_MOVES_FULL: {
                const curMove = this.experienceGainQueue[0].pokemon.newMoves[0];
                this.actions = [
                    this.ag.readText(
                        `${this.experienceGainQueue[0].pokemon.name} wants to learn the move ${curMove.name}.`
                    ),
                    this.ag.finishReading(),
                    this.ag.readText(
                        `But ${this.experienceGainQueue[0].pokemon.name} already knows 4 moves.`
                    ),
                    this.ag.finishReading(),
                    this.ag.readText(`Should a move be deleted to make room for ${curMove.name}?`),
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'Yes or no make room for new move',
                        startAction: () => {
                            d.yesOrNoMenu.visible = true;
                            this.yesOrNoOption = 0;
                        },
                        action: () => {
                            this.yesOrNoOption = this.awaitPokemonOptionInput(
                                this.yesOrNoOption,
                                2
                            ).currentOption;
                            return this.awaitPokemonOptionInput(this.yesOrNoOption, 2).selected;
                        },
                        endAction: () => {
                            d.yesOrNoMenu.visible = false;
                        },
                    }),
                ];

                break;
            }

            case STOP_LEARNING_NEW_MOVE:
                this.actions = [
                    this.ag.readText(
                        `Stop trying to learn ${this.experienceGainQueue[0].pokemon.newMoves[0].name}?`
                    ),
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'Stop trying to learn new move yes or no',
                        startAction: () => {
                            d.yesOrNoMenu.visible = true;
                            this.yesOrNoOption = 0;
                        },
                        action: () => {
                            this.yesOrNoOption = this.awaitPokemonOptionInput(
                                this.yesOrNoOption,
                                2
                            ).currentOption;
                            return this.awaitPokemonOptionInput(this.yesOrNoOption, 2).selected;
                        },
                        endAction: () => {
                            d.yesOrNoMenu.visible = false;
                        },
                    }),
                ];
                break;
            case CHOOSE_MOVE_TO_FORGET:
                this.actions = [
                    this.ag.readText('Which move should be forgotten?'),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can select move to forget.',
                        startAction: () => {
                            d.newMoveMenu.pokemon = this.experienceGainQueue[0].pokemon;
                            d.newMoveMenu.newMove = this.experienceGainQueue[0].pokemon.newMoves[0];
                            d.newMoveMenu.visible = true;
                        },
                        action: () => {
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            const maxOptions = 5;
                            this.summaryMoveDetailOption = this.awaitVerticalOptionInput(
                                this.summaryMoveDetailOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitVerticalOptionInput(this.summaryMoveDetailOption, maxOptions)
                                .selected;
                        },
                        endAction: () => {
                            d.newMoveMenu.visible = false;
                        },
                    }),
                ];
                break;
            case FORGOT_MOVE:
                this.actions = [
                    this.ag.readText(
                        `1... 2... 3... and... Poof! ${this.experienceGainQueue[0].pokemon.name} forgot ${
                            this.experienceGainQueue[0].pokemon.moves[this.summaryMoveDetailOption].name
                        }!`
                    ),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                ];
                break;
            case LEARNED_NEW_MOVE:
                this.actions = [
                    this.ag.script(() => {
                        if (!this.experienceGainQueue[0].pokemon.hasEmptyMoveSlot()) {
                            this.experienceGainQueue[0].pokemon.moves[this.summaryMoveDetailOption] = null;
                        }
                        this.experienceGainQueue[0].pokemon.learnMove(
                            this.experienceGainQueue[0].pokemon.newMoves[0]
                        );
                    }),
                    this.ag.readText(
                        `${this.experienceGainQueue[0].pokemon.name} learned ${this.experienceGainQueue[0].pokemon.newMoves[0].name}!`
                    ),
                    this.ag.script(() => {
                        this.experienceGainQueue[0].pokemon.newMoves.shift();
                    }),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                ];

                break;

            case DID_NOT_LEARN_NEW_MOVE:
                this.actions = [
                    this.ag.readText(
                        `${this.experienceGainQueue[0].pokemon.name} did not learn ${this.experienceGainQueue[0].pokemon.newMoves[0].name}.`
                    ),
                    this.ag.finishReading(),
                    this.ag.script(() => {
                        this.experienceGainQueue[0].pokemon.newMoves.shift();
                    }),
                ];
                break;
            case PLAYER_FAINTS:
                this.actions = [
                    this.ag.playAudio(
                        `audio/battle_cries/cries/${this.playerPokemon.id.toString().padStart(3, '0')}.mp3`
                    ),
                    this.ag.delay(50),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player pokemon fainting',
                        animationFunction: () => {
                            this.playerPokemon.backSprite.verticalOffset += 2;
                            this.playerPokemon.backSprite.verticalCrop += 2;
                            return this.playerPokemon.backSprite.verticalOffset === 128;
                        },
                        endAction: () => {
                            d.playerPokemon.visible = false;
                            d.playerInfo.visible = false;
                            // Clear identifiedBy when pokemon faints
                            this.playerPokemon.identifiedBy = null;
                            this.clearFieldEffectsForSource(this.playerPokemon);
                            this.playerPokemon.status1 = new Status({
                                id: 'faint',
                                startTurn: 0,
                                duration: Number.MAX_SAFE_INTEGER,
                                pokemon: this.playerPokemon,
                            });
                        },
                    }),
                    this.ag.readText(`${this.playerPokemon.name} fainted.`),
                    this.ag.finishReading(),
                ];

                break;
            case WHITED_OUT:
                this.actions = [
                    this.ag.readText(`${getOSM().player.name} ran out of usable pokemon...`),
                    this.ag.finishReading(),
                    this.ag.readText(`${getOSM().player.name} whited out!`),
                    this.ag.finishReading(),
                    this.ag.script(() => {
                        getOSM().player.surfing = false;
                        getOSM().player.biking = false;
                    }),
                ];
                break;
            case END_BATTLE:
                this.actions = [
                    this.ag.delay(30),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Darken screen',
                        startAction: () => {},
                        animationFunction: () => {
                            return this.battleGUI.darken(0.5);
                        },
                        endAction: () => {
                            // Clear identifiedBy and ability overrides when battle ends
                            this.playerPokemon.identifiedBy = null;
                            if (this.foePokemon) {
                                this.foePokemon.identifiedBy = null;
                                this.foePokemon.resetVolatileStatuses();
                            }
                            this.playerPokemon.resetBattleStages();
                            this.playerPokemon.resetVolatileStatuses();
                            if (this.foeTrainer !== null) {
                                this.foeTrainer.isDefeated = true;
                                if (this.player.whitedOut()) {
                                    this.foeTrainer.healParty();
                                    this.foeTrainer.isDefeated = false;
                                    if (getOSM().getCurrentEvent()) {
                                        getOSM().getCurrentEvent().reset();
                                        getOSM().clearActions();
                                    }
                                }
                            }
                            this.player.overworldSprite.moving = true;
                            d.foePokemon.scale = 1;
                            d.foePokemon.visible = true;
                            d.foePokemon.verticalCrop = 0;
                            d.foePokemon.verticalOffset = 0;
                            d.playerPokemon.scale = 1;
                            d.playerPokemon.visible = true;
                            d.playerPokemon.verticalCrop = 0;
                            d.playerPokemon.verticalOffset = 0;
                            if (!options.skipEndBattle) {
                                getOSM().currentState = END_BATTLE;
                            }
                            const osm = getOSM();
                            osm.battleStateMachine.foeTrainer = null;
                            osm.context.npcSeenBy = null;
                            const restoredParty = new Array(this.player.party.length);
                            for (let i = 0; i < this.player.party.length; i++) {
                                restoredParty[this.partyOrder[i]] = this.player.party[i];
                            }
                            this.player.party = restoredParty;
                            getOSM().context.inBattle = false;
                            // animateOverworldV2();
                        },
                    }),
                ];
                break;
            case END_TRAINER_BATTLE:
                this.actions = [
                    this.ag.readText(`You defeated ${this.foeTrainer.name}!`),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Trainer animates back in',
                        startAction: () => {
                            d.foeTrainer.visible = true;
                            d.foeTrainer.position.x = 240;
                        },
                        animationFunction: () => {
                            d.foeTrainer.position.x -= 1 * ANIMATION_SPEED;
                            return d.foeTrainer.position.x === 160;
                        },
                    }),
                    this.ag.readText(`${this.foeTrainer.postBattleText}`),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                    this.ag.readText(`You won \u20BD${this.foeTrainer.battleReward}!`),
                    this.ag.finishReading(),
                    this.ag.script(() => {
                        getOSM().player.money += this.foeTrainer.battleReward;
                    }),
                    this.ag.clearText(),
                ];
                break;
            case RUN_AWAY:
                if (this.foeTrainer !== null) {
                    this.actions = [
                        this.ag.script(() => {
                            this.battleGUI.text.visible = true;
                        }),
                        this.ag.readText("You can't run away from a trainer battle!"),
                        this.ag.finishReading(),
                    ];
                    break;
                }
                this.actions = [
                    this.ag.clearText(),
                    this.ag.script(() => {
                        this.battleGUI.text.visible = true;
                    }),
                    this.ag.readText('Ran away safely!'),
                    this.ag.finishReading(),
                ];
                break;
            case POKEMON_MENU_BATTLE: {
                const mustChoose = this.playerPokemon.stats.hp === 0;
                this.actions = [];
                this.actions.push(
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a pokemon',
                        battleStateMachine: this,
                        startAction: () => {
                            this.battleText = '';
                            this.battleGUI.text.setColor('white');
                            d.overworldTextBox.visible = false;
                        },
                        action: () => {
                            if (!mustChoose && this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            d.yesOrNoMenu.visible = false;
                            d.pokemonMenu.visible = true;
                            d.pokemonMenu.sprites.pokemonOptionsBox.visible = false;
                            this.pokemonMenuText = 'Choose a pokemon.';
                            const maxOptions = mustChoose
                                ? this.player.party.length
                                : this.player.party.length + 1;
                            this.pokemonOption = this.awaitPokemonOptionInput(
                                this.pokemonOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitPokemonOptionInput(Math.floor(this.pokemonOption), maxOptions)
                                .selected;
                        },
                        endAction: () => {},
                    })
                );
                break;
            }
            case CHOOSE_NEXT_POKEMON_QUESTION:
                this.actions = [
                    this.ag.readText('Choose next pokemon?'),
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a pokemon',
                        battleStateMachine: this,
                        debounce: 30,
                        action: () => {
                            d.yesOrNoMenu.visible = true;
                            this.pokemonMenuText = 'Choose a pokemon.';
                            this.yesOrNoOption = this.awaitPokemonOptionInput(
                                this.yesOrNoOption,
                                2
                            ).currentOption;
                            return this.awaitPokemonOptionInput(this.yesOrNoOption, 2).selected;
                        },
                        endAction: () => {},
                    }),
                ];

                break;
            case POKEMON_PRESELECTED:
                this.actions.push(
                    new BattleAction({
                        type: ActionType.CODE,
                        startAction: () => {
                            d.pokemonMenu.sprites.pokemonOptionsBox.visible = true;
                            this.pokemonDetailOption = 0;
                        },
                        action: () => {
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                this.pokemonDetailOption = 0;
                                return true;
                            }
                            const maxOptions = 3;
                            this.pokemonDetailOption = this.awaitPokemonOptionInput(
                                this.pokemonDetailOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitPokemonOptionInput(
                                Math.floor(this.pokemonDetailOption),
                                maxOptions
                            ).selected;
                        },
                        endAction: () => {
                            d.pokemonMenu.sprites.pokemonOptionsBox.visible = false;
                        },
                    })
                );
                break;
            case SWITCHED_TO_SAME_POKEMON:
                this.actions = [
                    this.ag.script(() => {
                        d.overworldTextBox.visible = true;
                        this.battleGUI.text.setColor('black');
                    }),
                    this.ag.readText(`But ${this.playerPokemon.name} is already in battle!`),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                ];
                break;
            case SWITCHED_TO_FAINTED_POKEMON:
                this.actions = [
                    this.ag.script(() => {
                        d.overworldTextBox.visible = true;
                        this.battleGUI.text.setColor('black');
                    }),
                    this.ag.readText(`${this.playerPokemon.name} has no energy left to battle!`),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                ];
                break;
            case POKEMON_SWITCHED_END_OF_TURN:
                this.upcomingPokemon = this.player.party[this.pokemonOption];
                this.player.party[this.pokemonOption] = this.player.party[0];
                this.player.party[0] = this.upcomingPokemon;
                [this.partyOrder[0], this.partyOrder[this.pokemonOption]] = [
                    this.partyOrder[this.pokemonOption],
                    this.partyOrder[0],
                ];
                this.actions = [
                    this.ag.hideUIElement(d.pokemonMenu),
                    this.ag.script(() => {
                        // Clear identifiedBy for both pokemon when switching
                        this.playerPokemon.identifiedBy = null;
                        this.foePokemon.identifiedBy = null;
                        this.playerPokemon = this.upcomingPokemon;
                        // Clear identifiedBy for the new pokemon as well
                        this.playerPokemon.identifiedBy = null;
                        this.participantPokemon.add(this.playerPokemon);
                    }),
                    this.ag.readText(`Go ${this.upcomingPokemon.nickname || this.upcomingPokemon.name}!`, {
                        delay: 30,
                        showContinueArrow: false,
                    }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player throwing ball from the left side of the screen',
                        startAction: () => {
                            d.thrownPokeball = this.upcomingPokemon.pokeball.battleSprites.thrown;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.frames.val = 0;
                            d.thrownPokeball.moving = false;
                            d.thrownPokeball.position.x = -12;
                            d.thrownPokeball.position.y = d.player.position.y + 8;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.yVelocity = -1.3 * ANIMATION_SPEED;
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            // d.thrownPokeball.setImage(d.images.thrownPokeballImage);
                            // d.thrownPokeball.frames.max = 8;
                            // d.thrownPokeball.hold = 2;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.constantRotationSpeed = -30;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.moving = false;
                            d.thrownPokeball.frames.val = 0;
                            d.thrownPokeball.position.x += 0.6 * ANIMATION_SPEED;
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += d.thrownPokeball.yVelocity;
                            return d.thrownPokeball.position.y > 112;
                        },
                        endAction: () => {
                            d.thrownPokeball.resetRotation();
                        },
                    }),
                    this.ag.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player pokemon spawning from pokeball',
                        startAction: () => {
                            d.playerPokemon.scale = 0;
                            d.playerPokemon.visible = false;
                            d.playerPokemon.verticalCrop = 0;
                            d.playerPokemon.verticalOffset = 0;
                            d.whiteFlash.flash();
                        },
                        animationFunction: () => {
                            d.playerPokemon.visible = true;
                            if (d.playerPokemon.scale + SPAWN_SCALE_SPEED * ANIMATION_SPEED < 1) {
                                d.playerPokemon.scale += SPAWN_SCALE_SPEED * ANIMATION_SPEED;
                            } else {
                                d.playerPokemon.scale = 1;
                            }
                            d.playerPokemon.position.x = 32 + 32 * (1 - d.playerPokemon.scale);
                            d.playerPokemon.position.y =
                                48 + 32 * (1 - d.playerPokemon.scale) + 32 * (1 - d.playerPokemon.scale);
                            return d.playerPokemon.scale === 1;
                        },
                    }),
                    this.ag.playBattleCry(this.upcomingPokemon),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player information box from the right side of the screen',
                        startAction: () => {
                            d.playerInfo.visible = true;
                            d.playerInfo.position.x = 240;
                        },
                        animationFunction: () => {
                            d.playerInfo.position.x -= 2 * ANIMATION_SPEED;
                            return d.playerInfo.position.x <= 240 - 96 - 16;
                        },
                    }),
                    this.ag.delay(120),
                ];
                break;
            case POKEMON_SWITCHED:
                this.upcomingPokemon = this.player.party[this.pokemonOption];
                this.player.party[this.pokemonOption] = this.player.party[0];
                this.player.party[0] = this.upcomingPokemon;

                [this.partyOrder[0], this.partyOrder[this.pokemonOption]] = [
                    this.partyOrder[this.pokemonOption],
                    this.partyOrder[0],
                ];

                this.actions.push(
                    this.ag.script(() => {
                        this.upcomingPokemon.loadBattleCry();
                    }),
                    this.ag.hideUIElement(d.pokemonMenu),
                    this.ag.readText(`That's enough, ${this.playerPokemon.name}!`, { delay: 50 }),
                    this.ag.playAudio(`audio/sfx/pokemon_enters_pokeball.mp3`),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player pokemon shrinking into pokeball',
                        startAction: () => {
                            d.whiteFlash.flash();
                        },
                        animationFunction: () => {
                            d.playerPokemon.scale -= SPAWN_SCALE_SPEED * ANIMATION_SPEED;
                            d.playerPokemon.position.x = 32 + 32 * (1 - d.playerPokemon.scale);
                            d.playerPokemon.position.y =
                                48 + 32 * (1 - d.playerPokemon.scale) + 32 * (1 - d.playerPokemon.scale);
                            if (d.playerPokemon.scale <= 0) {
                                d.playerPokemon.visible = false;
                            }
                            return d.playerPokemon.scale <= 0;
                        },
                        endAction: () => {
                            // Switch pokemon for upcoming pokemon
                            d.playerInfo.visible = false;
                            d.playerPokemon.visible = false;
                            // Clear identifiedBy for both pokemon when switching
                            this.playerPokemon.identifiedBy = null;
                            this.foePokemon.identifiedBy = null;
                            this.clearFieldEffectsForSource(this.playerPokemon);
                            this.playerPokemon.resetVolatileStatuses();
                            this.playerPokemon.resetBattleStages();
                            this.playerPokemon = this.upcomingPokemon;
                            // Clear identifiedBy for the new pokemon as well
                            this.playerPokemon.identifiedBy = null;
                            this.participantPokemon.add(this.playerPokemon);
                        },
                    }),
                    this.ag.hideUIElement(d.playerPokemon),
                    this.ag.readText(`Go ${this.upcomingPokemon.nickname || this.upcomingPokemon.name}!`, {
                        delay: 30,
                        showContinueArrow: false,
                    }),
                    ...this.ag.sendOutPlayerPokemon(this.upcomingPokemon)
                );
                break;
            case SUMMARY_MENU_BATTLE:
                this.actions = [
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can navigate through the summary menu',
                        startAction: () => {
                            d.pokemonSummary.visible = true;
                        },
                        action: () => {
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                this.summaryOption = 0;
                                return true;
                            }
                            const maxOptions = 3;
                            this.summaryOption = this.awaitHorizontalOptionInput(
                                this.summaryOption,
                                maxOptions
                            ).currentOption;
                            this.pokemonOption = this.awaitVerticalOptionInput(
                                this.pokemonOption,
                                this.player.party.length
                            ).currentOption;
                            return this.awaitHorizontalOptionInput(this.summaryOption, maxOptions).selected;
                        },
                        endAction: () => {
                            if (this.shouldCancel) {
                                d.pokemonSummary.visible = false;
                                d.showMoveSummary = false;
                            }
                            if (this.summaryOption !== 2) {
                                d.pokemonSummary.visible = false;
                            } else {
                                this.showMoveSummary = true;
                            }
                        },
                    }),
                ];
                break;
            case SUMMARY_MOVE_DETAIL:
                this.actions = [
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can navigate through the move detail menu',
                        startAction: () => {
                            d.pokemonSummary.visible = true;
                        },
                        action: () => {
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            const maxOptions = 5;
                            this.summaryMoveDetailOption = this.awaitVerticalOptionInput(
                                this.summaryMoveDetailOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitVerticalOptionInput(this.summaryMoveDetailOption, maxOptions)
                                .selected;
                        },
                        endAction: () => {
                            this.showMoveSummary = false;
                        },
                    }),
                ];
                break;
            case BAG_MENU:
                getOSM().menuHandler.handleBagMenuBattle(this);

                break;
            case BAG_ITEM_PRESELECTED:
                this.actions = [
                    new BattleAction({
                        type: ActionType.CODE,
                        description: 'Bag item has been preselected. What to do with it?',
                        startAction: () => {
                            d.bagMenu.sprites.bagOptionsBox.visible = true;
                            this.bagDetailOption = 0;
                            this.selectedItem = this.bagMenu.createItemFromCurrentOption();
                            d.bagMenu.sprites.bagOptionsBox.setOptions(this.selectedItem.battleOptions);
                        },
                        action: () => {
                            const maxOptions = this.selectedItem.battleOptions.length;
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            this.bagDetailOption = this.awaitPokemonOptionInput(
                                this.bagDetailOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitPokemonOptionInput(Math.floor(this.bagDetailOption), maxOptions)
                                .selected;
                        },
                        endAction: () => {
                            d.bagMenu.sprites.bagOptionsBox.visible = false;
                        },
                    }),
                ];
                break;
            case SELECT_POKEMON_TO_APPLY_ITEM:
                this.actions = [];
                this.actions.push(
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a pokemon to apply an item to',
                        battleStateMachine: this,
                        // debounce: 30,
                        startAction: () => {
                            this.battleText = '';
                            this.battleGUI.text.setColor('white');
                            d.overworldTextBox.visible = false;
                        },
                        action: () => {
                            if (this.awaitCancelInput()) {
                                this.shouldCancel = true;
                                return true;
                            }
                            d.yesOrNoMenu.visible = false;
                            d.pokemonMenu.visible = true;
                            d.bagMenu.visible = false;
                            d.pokemonMenu.sprites.pokemonOptionsBox.visible = false;
                            this.pokemonMenuText = 'Use on which pokemon?';
                            const maxOptions = this.player.party.length + 1; // Not length - 1 because we need an option for the cancel button
                            this.pokemonOption = this.awaitPokemonOptionInput(
                                this.pokemonOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitPokemonOptionInput(Math.floor(this.pokemonOption), maxOptions)
                                .selected;
                        },
                        endAction: () => {},
                    })
                );
                break;
            case SELECT_MOVE_TO_APPLY_ITEM:
                this.actions = [
                    new BattleAction({
                        type: ActionType.INPUT,
                        description: 'User can now select a move to apply an item to',
                        battleStateMachine: this,
                        debounce: 30,
                        startAction: () => {
                            this.battleText = '';
                            this.battleGUI.text.setColor('white');
                            d.overworldTextBox.visible = false;
                            const curPokemonMoves = this.player.party[this.pokemonOption].moves;
                            const m0 = curPokemonMoves[0]?.name ?? '--';
                            const m1 = curPokemonMoves[1]?.name ?? '--';
                            const m2 = curPokemonMoves[2]?.name ?? '--';
                            const m3 = curPokemonMoves[3]?.name ?? '--';
                            d.pokemonMenu.sprites.moveOptionsBox.options = [m0, m1, m2, m3];
                            if (!['elixir', 'max_elixir'].includes(this.selectedItem.id)) {
                                d.pokemonMenu.sprites.moveOptionsBox.visible = true;
                            }
                        },
                        action: () => {
                            if (['elixir', 'max_elixir'].includes(this.selectedItem.id)) {
                                d.pokemonMenu.sprites.moveOptionsBox.visible = false;
                                return true;
                            }
                            d.yesOrNoMenu.visible = false;
                            d.pokemonMenu.visible = true;
                            d.bagMenu.visible = false;
                            d.pokemonMenu.sprites.moveOptionsBox.visible = true;
                            this.pokemonMenuText = 'Use on which move?';
                            const maxOptions = 4;
                            this.moveItemOption = this.awaitPokemonOptionInput(
                                this.moveItemOption,
                                maxOptions
                            ).currentOption;
                            return this.awaitPokemonOptionInput(Math.floor(this.moveItemOption), maxOptions)
                                .selected;
                        },
                        endAction: () => {
                            if (['elixir', 'max_elixir'].includes(this.selectedItem.id)) {
                                this.player.party[this.pokemonOption].moves[0]?.applyItem(
                                    this.selectedItem.id
                                );
                                this.player.party[this.pokemonOption].moves[1]?.applyItem(
                                    this.selectedItem.id
                                );
                                this.player.party[this.pokemonOption].moves[2]?.applyItem(
                                    this.selectedItem.id
                                );
                                this.player.party[this.pokemonOption].moves[3]?.applyItem(
                                    this.selectedItem.id
                                );
                                this.player.useItemV2(this.selectedItem.id);
                                return;
                            }
                            this.player.party[this.pokemonOption].moves[this.moveItemOption].applyItem(
                                this.selectedItem.id
                            );
                            this.player.useItemV2(this.selectedItem.id);
                        },
                    }),
                ];
                break;
            case ITEM_APPLIED: {
                const isPinchBerry = pinchBerryItemIds.includes(this.selectedItem.id);
                const isXItem = xItemIds.includes(this.selectedItem.id);
                const isBattleItem = battleItemIds.includes(this.selectedItem.id);
                const statBoostStat = isPinchBerry
                    ? pinchBerryStat[this.selectedItem.id]
                    : isXItem
                    ? xItemStat[this.selectedItem.id]
                    : null;
                const statNames = {
                    attack: 'Attack',
                    defense: 'Defense',
                    specialAttack: 'Sp. Atk',
                    specialDefense: 'Sp. Def',
                    speed: 'Speed',
                    accuracy: 'Accuracy',
                };
                const pokemonToAffect = this.player.party[this.pokemonOption];

                let appliedMessage;
                if (isXItem) {
                    appliedMessage = `${pokemonToAffect.name}'s ${statNames[statBoostStat]} rose sharply!`;
                } else if (isPinchBerry) {
                    appliedMessage = `${pokemonToAffect.name}'s ${statNames[statBoostStat]} rose!`;
                } else if (this.selectedItem.id === 'guard_spec') {
                    appliedMessage = `${pokemonToAffect.name} is protected against stat changes!`;
                } else if (this.selectedItem.id === 'dire_hit') {
                    appliedMessage = `${pokemonToAffect.name} is getting pumped!`;
                } else {
                    appliedMessage = `${pokemonToAffect.name}'s ${
                        moveItemIDs.includes(this.selectedItem.id) ? 'PP' : 'health'
                    } was restored!`;
                }

                let middleActions;
                if (isXItem) {
                    middleActions = [
                        this.ag.script(() => {
                            const p = this.player.party[this.pokemonOption];
                            p.battleStages[statBoostStat] = Math.min(6, p.battleStages[statBoostStat] + 2);
                        }),
                        ...this.ag.animateStatChange(pokemonToAffect, { [statBoostStat]: 2 }),
                    ];
                } else if (isPinchBerry) {
                    middleActions = [
                        this.ag.script(() => {
                            const p = this.player.party[this.pokemonOption];
                            p.battleStages[statBoostStat] = Math.min(6, p.battleStages[statBoostStat] + 1);
                        }),
                        ...this.ag.animateStatChange(pokemonToAffect, { [statBoostStat]: 1 }),
                    ];
                } else if (this.selectedItem.id === 'guard_spec') {
                    middleActions = [
                        this.ag.script(() => {
                            pokemonToAffect.volatileStatuses.guardSpec = 5;
                        }),
                    ];
                } else if (this.selectedItem.id === 'dire_hit') {
                    middleActions = [
                        this.ag.script(() => {
                            pokemonToAffect.volatileStatuses.pumped = 2;
                        }),
                    ];
                } else {
                    middleActions = [
                        (() => {
                            let healAmount = 0;
                            return new BattleAction({
                                type: ActionType.ANIMATION,
                                description: 'Animate item application.',
                                animationFunction: () => {
                                    const pokemonToHeal = this.player.party[this.pokemonOption];
                                    const targetHealAmount =
                                        this.selectedItem.id === 'revive'
                                            ? Math.round(pokemonToHeal.stats.maxHp / 2)
                                            : this.selectedItem.healAmount;

                                    if (
                                        !!pokemonToHeal.status1 &&
                                        this.selectedItem.healStatus?.includes(pokemonToHeal.status1.id)
                                    ) {
                                        pokemonToHeal.status1 = null;
                                    }

                                    if (
                                        healAmount < targetHealAmount &&
                                        pokemonToHeal.stats.hp < pokemonToHeal.stats.maxHp
                                    ) {
                                        pokemonToHeal.stats.hp += 1;
                                        healAmount += 1;
                                    } else {
                                        return true;
                                    }
                                },
                                endAction: () => {},
                            });
                        })(),
                    ];
                }

                if (isBattleItem) {
                    const itemName = this.selectedItem.name || this.selectedItem.id;
                    this.actions = [
                        this.ag.script(() => {
                            d.bagMenu.visible = false;
                            d.pokemonMenu.visible = false;
                        }),
                        this.ag.readText(`${this.player.name} used a ${itemName}!`),
                        this.ag.finishReading(),
                        this.ag.script(() => {
                            this.player.useItemV2(this.selectedItem.id);
                        }),
                        ...middleActions,
                        this.ag.readText(appliedMessage),
                        this.ag.finishReading(),
                        this.ag.clearText(),
                        this.ag.script(() => {
                            this.selectedItem = {};
                        }),
                    ];
                } else {
                    this.actions = [
                        this.ag.script(() => {
                            this.player.useItemV2(this.selectedItem.id);
                        }),
                        ...middleActions,
                        this.ag.showUIElement(d.overworldTextBox),
                        this.ag.script(() => {
                            this.battleGUI.text.setColor('black');
                            this.battleGUI.text.visible = true;
                        }),
                        this.ag.readText(appliedMessage),
                        this.ag.finishReading(),
                        this.ag.clearText(),
                        this.ag.script(() => {
                            this.battleGUI.text.text = '';
                            d.pokemonMenu.visible = false;
                            d.bagMenu.visible = false;
                            d.overworldTextBox.visible = false;
                            this.battleGUI.text.setColor('white');
                            this.selectedItem = {};
                        }),
                    ];
                }
                break;
            }
            case IT_WONT_HAVE_ANY_EFFECT:
                this.actions = [
                    this.ag.script(() => {
                        d.overworldTextBox.visible = true;
                        this.battleGUI.text.setColor('black');
                    }),
                    this.ag.readText("It won't have any effect..."),
                    this.ag.finishReading(),
                    this.ag.script(() => {
                        this.battleGUI.text.text = '';
                        d.pokemonMenu.visible = false;
                        d.bagMenu.visible = false;
                        d.overworldTextBox.visible = false;
                        this.battleGUI.text.setColor('white');
                    }),
                ];
                break;
            case CATCH_POKEMON_ATTEMPT: {
                d.bagMenu.visible = false;

                // Check for pokeball never fails cheat
                const optionsMenu = getOSM()?.optionsMenu;
                const pokeballNeverFails = optionsMenu?.battleCheatPokeballNeverFails === 'on';

                // Catch rate formula
                const effectiveCatchRate = this.foePokemon.catchRate(this.selectedItem);

                let numShakes = 0;
                if (pokeballNeverFails) {
                    // If cheat is enabled, always get 4 shakes (guaranteed catch)
                    numShakes = 4;
                } else {
                    // Normal catch calculation
                    for (let i = 0; i < 4; i++) {
                        const randomNum = Math.floor(Math.random() * 65535);
                        const shakeProbability =
                            1048560 / Math.sqrt(Math.sqrt(16711680 / effectiveCatchRate));

                        if (randomNum < shakeProbability) {
                            numShakes += 1;
                        }
                    }
                }

                this.actions = [
                    this.ag.readText(`${this.player.name} used a ${this.selectedItem.name}!`),
                    this.ag.playAudio('audio/sfx/throw_pokeball.mp3'),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate player throwing ball from the left side of the screen',
                        startAction: () => {
                            this.player.useItemV2(this.selectedItem.id);
                            // d.thrownPokeball.setImage(d.images.thrownPokeballImage);
                            d.thrownPokeball = this.selectedItem.battleSprites.thrown;
                            d.thrownPokeball.moving = false;
                            // d.thrownPokeball.frames.max = 8;
                            d.thrownPokeball.position.x = -12;
                            d.thrownPokeball.position.y = d.player.position.y + 8;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.yVelocity = -3 * ANIMATION_SPEED;
                            d.thrownPokeball.gravity = 0.06 * ANIMATION_SPEED ** 2;
                            d.foePokemon.scale = 1;
                            d.foePokemon.visible = true;
                            d.foePokemon.position.x = 144;
                            d.foePokemon.position.y = 16;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.position.x += 2.5 * ANIMATION_SPEED;
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += d.thrownPokeball.yVelocity;
                            return d.thrownPokeball.position.x > 240 - 64 - 6; // (Width of pokemon + width of ball)
                        },
                        endAction: () => {
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.yVelocity = 0;
                            d.thrownPokeball.gravity = 0;
                            d.thrownPokeball.frames.val = 0;
                            d.thrownPokeball.moving = false;
                            d.thrownPokeball.position.x = 144 + 32 - 6;
                            d.thrownPokeball.position.y = Math.ceil(d.thrownPokeball.position.y);
                        },
                    }),
                    this.ag.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball opening',
                        startAction: () => {
                            // d.thrownPokeball.setImage(d.images.pokeballOpeningImage);
                            this.selectedItem.battleSprites.open.position = {
                                x: d.thrownPokeball.position.x,
                                y: d.thrownPokeball.position.y,
                            };
                            d.thrownPokeball = this.selectedItem.battleSprites.open;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.moving = true;
                            d.thrownPokeball.hold = 8;
                            d.thrownPokeball.frames.max = 3;
                            d.thrownPokeball.frames.val = 0;
                            d.thrownPokeball.frames.stopAtEnd = true;
                            d.whiteFlash.flash();
                        },
                        animationFunction: () => {
                            d.thrownPokeball.frames.max = 3;
                            if (d.thrownPokeball.frames.val === 2) {
                                d.thrownPokeball.moving = false;
                                d.thrownPokeball.frames.val = 2;
                                return true;
                            }
                        },
                        endAction: () => {
                            this.selectedItem.battleSprites.close.position = {
                                x: d.thrownPokeball.position.x,
                                y: d.thrownPokeball.position.y,
                            };
                            d.thrownPokeball = this.selectedItem.battleSprites.close;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.moving = false;
                            d.thrownPokeball.frames.val = 0;
                        },
                    }),
                    this.parallel([
                        this.ag.delay(32),
                        new BattleAction({
                            type: ActionType.ANIMATION,
                            description: 'Animate ball shrinking pokemon',
                            startAction: () => {
                                d.foePokemon.scale = 1;
                                d.foePokemon.visible = true;
                                d.foePokemon.position.x = 144;
                                d.foePokemon.position.y = 16;
                            },
                            animationFunction: () => {
                                d.foePokemon.scale -= SPAWN_SCALE_SPEED * ANIMATION_SPEED;
                                d.foePokemon.position.x = 240 - 64 - 32 + 32 * (1 - d.foePokemon.scale);
                                d.foePokemon.position.y = 16 + 8 * (1 - d.foePokemon.scale);
                                return d.foePokemon.scale <= 0;
                            },
                            endAction: () => {
                                d.foePokemon.visible = false;
                            },
                        }),
                        this.ag.playAudio('audio/sfx/pokemon_enters_pokeball.mp3', { delay: 15 }),
                    ]),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball closing',
                        startAction: () => {
                            this.selectedItem.battleSprites.close.position = {
                                x: d.thrownPokeball.position.x,
                                y: d.thrownPokeball.position.y,
                            };
                            d.thrownPokeball = this.selectedItem.battleSprites.close;
                            d.thrownPokeball.visible = true;
                            d.thrownPokeball.moving = true;
                            d.thrownPokeball.stopAtEnd = true;
                            d.thrownPokeball.hold = 8;
                            d.thrownPokeball.frames.max = 3;
                        },
                        animationFunction: () => {
                            if (d.thrownPokeball.frames.val === 2) {
                                d.thrownPokeball.moving = false;
                                d.thrownPokeball.frames.val = 2;
                            }
                            return d.thrownPokeball.frames.val === 2;
                        },
                    }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball falling to ground after shrinking pokemon.',
                        startAction: () => {
                            this.selectedItem.battleSprites.open.position = {
                                x: d.thrownPokeball.position.x,
                                y: d.thrownPokeball.position.y,
                            };
                            d.thrownPokeball = this.selectedItem.battleSprites.open;
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            d.thrownPokeball.yVelocity = 0;
                            // d.thrownPokeball.setImage(d.images.pokeballShakeImage);
                            // d.thrownPokeball.frames.max = 96;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += d.thrownPokeball.yVelocity;
                            d.thrownPokeball.moving = false;
                            d.thrownPokeball.frames.val = 0;
                            return d.thrownPokeball.position.y > 52;
                        },
                        endAction: () => {},
                    }),
                    this.ag.playAudio('audio/sfx/pokeball_bounce_1.mp3', { delay: 15 }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball first bounce',
                        startAction: () => {
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            d.thrownPokeball.yVelocity = -0.8 * ANIMATION_SPEED;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += Math.round(d.thrownPokeball.yVelocity);
                            d.thrownPokeball.position.y = Math.ceil(d.thrownPokeball.position.y);
                            return d.thrownPokeball.position.y > 52 && d.thrownPokeball.yVelocity > 0;
                        },
                        endAction: () => {},
                    }),
                    this.ag.playAudio('audio/sfx/pokeball_bounce_2.mp3', { delay: 15 }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball second bounce',
                        startAction: () => {
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            d.thrownPokeball.yVelocity = -0.5 * ANIMATION_SPEED;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += Math.round(d.thrownPokeball.yVelocity);
                            d.thrownPokeball.position.y = Math.ceil(d.thrownPokeball.position.y);
                            return d.thrownPokeball.position.y > 52 && d.thrownPokeball.yVelocity > 0;
                        },
                        endAction: () => {},
                    }),
                    this.ag.playAudio('audio/sfx/pokeball_bounce_3.mp3', { delay: 15 }),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball third bounce',
                        startAction: () => {
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            d.thrownPokeball.yVelocity = -0.25 * ANIMATION_SPEED;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += Math.round(d.thrownPokeball.yVelocity);
                            d.thrownPokeball.position.y = Math.ceil(d.thrownPokeball.position.y);
                            return d.thrownPokeball.position.y > 52 && d.thrownPokeball.yVelocity > 0;
                        },
                        endAction: () => {},
                    }),
                    this.ag.playAudio('audio/sfx/pokeball_bounce_4.mp3', { delay: 15 }),
                    this.ag.delay(50),
                    new BattleAction({
                        type: ActionType.ANIMATION,
                        description: 'Animate ball third bounce',
                        startAction: () => {
                            d.thrownPokeball.gravity = 0.03 * ANIMATION_SPEED ** 2;
                            d.thrownPokeball.yVelocity = -0.125 * ANIMATION_SPEED;
                        },
                        animationFunction: () => {
                            d.thrownPokeball.yVelocity += d.thrownPokeball.gravity;
                            d.thrownPokeball.position.y += Math.round(d.thrownPokeball.yVelocity);
                            d.thrownPokeball.position.y = Math.ceil(d.thrownPokeball.position.y);
                            return d.thrownPokeball.position.y > 52 && d.thrownPokeball.yVelocity > 0;
                        },
                        endAction: () => {
                            d.thrownPokeball.position.y = Math.round(d.thrownPokeball.position.y);
                        },
                    }),
                    ...this.ag.animatePokeballShakes(this.selectedItem.battleSprites.open, numShakes),
                ];

                if (numShakes < 4) {
                    this.actions.push(
                        this.parallel([
                            new BattleAction({
                                type: ActionType.ANIMATION,
                                description: 'Animate ball opening',
                                startAction: () => {
                                    this.selectedItem.battleSprites.open.position = {
                                        x: d.thrownPokeball.position.x,
                                        y: d.thrownPokeball.position.y,
                                    };
                                    d.thrownPokeball = this.selectedItem.battleSprites.open;
                                    d.thrownPokeball.moving = true;
                                    d.thrownPokeball.hold = 8;
                                    d.thrownPokeball.frames.max = 3;
                                    d.thrownPokeball.frames.val = 0;
                                    d.thrownPokeball.stopAtEnd = true;
                                    d.thrownPokeball.visible = true;
                                    // d.thrownPokeball.setImage(d.images.pokeballOpeningImage);
                                },
                                animationFunction: () => {
                                    // d.thrownPokeball.frames.max = 3;
                                    // if (d.thrownPokeball.frames.val === 2) {
                                    //     d.thrownPokeball.moving = false;
                                    // }
                                    return d.thrownPokeball.frames.val === 2;
                                },
                            }),
                            this.ag.playAudio('audio/sfx/spawn_from_pokeball.mp3'),
                            this.ag.delay(32),
                            new BattleAction({
                                type: ActionType.ANIMATION,
                                description: 'Animate pokemon escaping pokeball',
                                startAction: () => {
                                    d.thrownPokeball.moving = false;
                                    d.foePokemon.scale = 0;
                                    d.foePokemon.visible = true;
                                    d.foePokemon.position.x = 144;
                                    d.foePokemon.position.y = 16 + 48;
                                    d.whiteFlash.flash();
                                },
                                animationFunction: () => {
                                    d.thrownPokeball.frames.val = 2;
                                    if (d.foePokemon.scale + SPAWN_SCALE_SPEED * ANIMATION_SPEED < 1) {
                                        d.foePokemon.scale += SPAWN_SCALE_SPEED * ANIMATION_SPEED;
                                    } else {
                                        d.foePokemon.scale = 1;
                                    }
                                    d.foePokemon.position.x = 240 - 64 - 32 + 32 * (1 - d.foePokemon.scale);
                                    d.foePokemon.position.y = 16 + (48 - 48 * d.foePokemon.scale);
                                    if (d.foePokemon.scale === 1) {
                                        d.foePokemon.position.x =
                                            240 - 64 - 32 + 32 * (1 - d.foePokemon.scale);
                                        d.foePokemon.position.y = 16;
                                        return true;
                                    }
                                },
                                endAction: () => {
                                    d.foePokemon.visible = true;
                                    d.thrownPokeball.visible = false;
                                },
                            }),
                            this.ag.playBattleCry(this.foePokemon),
                        ])
                    );
                }

                switch (numShakes) {
                    case 0:
                        this.actions.push(this.ag.readText('Oh no! The pokemon broke free!'));
                        break;
                    case 1:
                        this.actions.push(this.ag.readText(`Aww! It appeared to be caught!`));
                        break;
                    case 2:
                        this.actions.push(this.ag.readText(`Aargh! Almost had it!`));
                        break;
                    case 3:
                        this.actions.push(this.ag.readText(`Gah! It was so close, too!`));
                        break;
                    case 4:
                        this.actions.push(
                            this.ag.readText(`Gotcha! ${this.foePokemon.name} was caught!`),
                            this.ag.script(() => {
                                this.foePokemon.pokeball = this.selectedItem;
                                this.foePokemon.originalTrainer = this.player;
                            })
                        );
                        break;
                    default:
                        break;
                }
                this.actions.push(this.ag.finishReading(), this.ag.clearText(), this.ag.clearMenus());
                break;
            }
            case POKEMON_CAUGHT:
                this.actions = [];
                break;
            case DONT_BE_A_THIEF:
                this.actions = [
                    this.ag.script(() => {
                        d.overworldTextBox.visible = true;
                        this.battleGUI.text.setColor('black');
                    }),
                    this.ag.readText("Hey! No stealing other trainers' Pokémon!"),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                    this.ag.script(() => {
                        d.overworldTextBox.visible = false;
                        this.battleGUI.text.setColor('white');
                    }),
                ];
                break;
            case ADD_CAUGHT_POKEMON_TO_POKEDEX:
                this.actions = [
                    this.ag.script(() => {
                        this.player.pokedex.addCaughtPokemon(this.foePokemon);
                    }),
                    this.ag.readText(`${this.foePokemon.name}'s data was added to the pokedex!`),
                    this.ag.finishReading(),
                    this.ag.clearText(),
                    this.ag.showUIElement(d.addedToPokedexSummary),
                    this.ag.finishReading(),
                    this.ag.hideUIElement(d.addedToPokedexSummary),
                ];
                break;
            case ASK_FOR_NICKNAME:
                this.actions = [
                    // this.ag.readText(`Would you like to give a nickname to ${this.foePokemon.name}?`),
                    // new BattleAction({
                    //     type: ActionType.INPUT,
                    //     description: 'User can now select a nickname for the pokemon',
                    //     startAction: () => {
                    //         d.yesOrNoMenu.visible = true;
                    //         this.yesOrNoOption = 0;
                    //     },
                    //     action: () => {
                    //         this.yesOrNoOption = this.awaitPokemonOptionInput(
                    //             this.yesOrNoOption,
                    //             2
                    //         ).currentOption;
                    //         return this.awaitPokemonOptionInput(this.yesOrNoOption, 2).selected;
                    //     },
                    //     endAction: () => {
                    //         d.yesOrNoMenu.visible = false;
                    //     },
                    // }),
                ];
                break;
            case POKEMON_SENT_TO_PC:
                this.actions = [
                    this.ag.readText(`${this.foePokemon.name} was sent to the PC!`),
                    this.ag.finishReading(),
                ];
                break;
            case NICKNAME_INPUT:
                this.actions = [this.ag.readText(`Well too bad. The dev hasn't implemented that yet.`)];
                break;
            case NICKNAME_CONFIRMATION:
                this.actions = [];
                break;
        }

        this.actions.forEach((a) => {
            a.battleStateMachine = this;
        });
    }

    /**
     * Creates a parallel action executor that executes multiple actions simultaneously.
     * @param {...*} actions - Actions to execute in parallel
     * @returns {Object} A parallel action executor with execute() and ended properties
     */
    parallel(...actions) {
        // Flatten the actions array in case arrays were passed as arguments
        const actionsArray = actions.flat();

        const executor = {
            _isParallel: true,
            actions: actionsArray,
            ended: false,
        };

        executor.execute = () => {
            if (executor.ended) {
                return;
            }

            // Execute all actions in parallel
            executor.actions.forEach((action) => {
                // Handle nested parallel wrappers, series wrappers, or arrays
                if (action && typeof action === 'object' && action._isParallel) {
                    // It's a parallel wrapper - execute it
                    if (!action.ended) {
                        action.execute();
                    }
                } else if (action && typeof action === 'object' && action._isSeries) {
                    // It's a series wrapper - execute it
                    if (!action.ended) {
                        action.execute();
                    }
                } else if (Array.isArray(action)) {
                    // It's an array - execute sequentially (arrays are sequential by default)
                    if (!action._sequentialExecutor) {
                        action._sequentialExecutor = this._createSequentialExecutor(action);
                    }
                    action._sequentialExecutor.execute();
                    action.ended = action._sequentialExecutor.ended;
                } else {
                    // It's a regular action
                    if (!action.ended) {
                        action.execute();
                    }
                }
            });

            // Check if all actions are ended
            executor.ended = executor.actions.every((action) => {
                if (action && typeof action === 'object' && action._isParallel) {
                    return action.ended;
                } else if (action && typeof action === 'object' && action._isSeries) {
                    return action.ended;
                } else if (Array.isArray(action)) {
                    return action._sequentialExecutor ? action._sequentialExecutor.ended : false;
                }
                return action.ended;
            });
        };

        return executor;
    }

    /**
     * Creates a series action executor that executes multiple actions sequentially.
     * @param {...*} actions - Actions to execute in series
     * @returns {Object} A series action executor with execute() and ended properties
     */
    series(...actions) {
        // Flatten the actions array in case arrays were passed as arguments
        const actionsArray = actions.flat();

        // Create a sequential executor for the actions
        const executor = this._createSequentialExecutor(actionsArray);
        executor._isSeries = true;

        return executor;
    }

    /**
     * Creates a sequential executor for an array of actions.
     * @param {Array} actionsArray - Array of actions to execute sequentially
     * @returns {Object} A sequential executor with execute() and ended properties
     */
    _createSequentialExecutor(actionsArray) {
        const executor = {
            actions: actionsArray,
            currentIndex: 0,
            currentExecutor: null,
            ended: false,
        };

        executor.execute = () => {
            if (executor.ended) {
                return;
            }

            // Handle empty array
            if (executor.actions.length === 0) {
                executor.ended = true;
                return;
            }

            // Check if we've completed all actions
            if (executor.currentIndex >= executor.actions.length) {
                executor.ended = true;
                return;
            }

            const currentAction = executor.actions[executor.currentIndex];

            // Handle different action types
            if (currentAction && typeof currentAction === 'object' && currentAction._isParallel) {
                // It's a parallel wrapper
                if (!executor.currentExecutor) {
                    executor.currentExecutor = currentAction;
                }
                executor.currentExecutor.execute();

                if (executor.currentExecutor.ended) {
                    executor.currentIndex++;
                    executor.currentExecutor = null;
                }
            } else if (currentAction && typeof currentAction === 'object' && currentAction._isSeries) {
                // It's a series wrapper
                if (!executor.currentExecutor) {
                    executor.currentExecutor = currentAction;
                }
                executor.currentExecutor.execute();

                if (executor.currentExecutor.ended) {
                    executor.currentIndex++;
                    executor.currentExecutor = null;
                }
            } else if (Array.isArray(currentAction)) {
                // It's a nested array - execute sequentially
                if (!executor.currentExecutor) {
                    executor.currentExecutor = this._createSequentialExecutor(currentAction);
                }
                executor.currentExecutor.execute();

                if (executor.currentExecutor.ended) {
                    executor.currentIndex++;
                    executor.currentExecutor = null;
                }
            } else {
                // It's a regular action
                if (!currentAction.ended) {
                    currentAction.execute();
                }

                if (currentAction.ended) {
                    executor.currentIndex++;
                }
            }

            // Update ended status
            executor.ended = executor.currentIndex >= executor.actions.length;
        };

        return executor;
    }

    update() {
        if (keyPPressInstant) {
            const msg = `-- Context For ${this.currentState} --`;
            console.log(msg);
            console.log(this);
            console.log(Array(msg.length).fill('-').join(''));
        }
        this.availableItems = [
            this.player.healingItemsList(),
            this.player.berriesList(),
            this.player.pokeballsList(),
            this.player.battleItemsList(),
        ];
        if (this.currentAction) {
            // Check if it's a parallel wrapper
            if (
                this.currentAction &&
                typeof this.currentAction === 'object' &&
                this.currentAction._isParallel
            ) {
                // Execute parallel actions
                this.currentAction.execute();
                if (this.currentAction.ended) {
                    this.currentAction = this.actions.shift();
                }
            } else if (
                this.currentAction &&
                typeof this.currentAction === 'object' &&
                this.currentAction._isSeries
            ) {
                // Execute series actions (sequential)
                this.currentAction.execute();
                if (this.currentAction.ended) {
                    this.currentAction = this.actions.shift();
                }
            } else if (Array.isArray(this.currentAction)) {
                // It's an array - execute sequentially
                if (!this.currentAction._sequentialExecutor) {
                    this.currentAction._sequentialExecutor = this._createSequentialExecutor(
                        this.currentAction
                    );
                }
                this.currentAction._sequentialExecutor.execute();
                if (this.currentAction._sequentialExecutor.ended) {
                    this.currentAction = this.actions.shift();
                }
            } else {
                // It's a single action
                if (!this.currentAction.ended) {
                    this.currentAction.execute();
                }
                if (this.currentAction.ended) {
                    this.currentAction = this.actions.shift();
                }
            }
        } else {
            if (this.actions.length === 0) {
                // We have no more actions for this phase.
                // We move on to the next phase
                this.transitionToNextState();
            } else {
                // We grab the next action from the queue (could be a single action, an array, or a parallel wrapper)
                this.currentAction = this.actions.shift();
            }
        }
    }

    awaitOptionInput(option) {
        if (keyWPressInstant) {
            if (option === 2) {
                option = 0;
            } else if (option === 3) {
                option = 1;
            }
        } else if (keyAPressInstant) {
            if (option === 1) {
                option = 0;
            } else if (option === 3) {
                option = 2;
            }
        } else if (keySPressInstant) {
            if (option === 0) {
                option = 2;
            } else if (option === 1) {
                option = 3;
            }
        } else if (keyDPressInstant) {
            if (option === 0) {
                // If Fight
                option = 1; // Then move to Bag
            } else if (option === 2) {
                // If Pokemon
                option = 3; // Then move to Run
            }
        }
        return { currentOption: option, selected: keyLPressInstant }; // keys.space.pressed }
    }

    awaitHorizontalOptionInput(option, maxOptions) {
        if (keyAPressInstant) {
            option -= 1;
        } else if (keyDPressInstant) {
            option += 1;
        }

        if (option < 0) {
            option = 0;
        } else if (option > maxOptions - 1) {
            option = maxOptions - 1;
        }

        return { currentOption: option, selected: keyLPressInstant };
    }

    awaitCancelInput() {
        return keyKPressInstant;
    }

    awaitVerticalOptionInput(option, maxOptions) {
        if (keyWPressInstant) {
            option -= 1;
        } else if (keySPressInstant) {
            option += 1;
        }

        if (option < 0) {
            option = 0;
        } else if (option > maxOptions - 1) {
            option = maxOptions - 1;
        }
        return { currentOption: option, selected: keyLPressInstant };
    }

    awaitPokemonOptionInput(option, maxOptions) {
        if (keyWPressInstant) {
            option -= 1;
        } else if (keySPressInstant) {
            option += 1;
        } else if (keyDPressInstant) {
            if (option === 0) {
                option = 1;
            }
        } else if (keyAPressInstant) {
            if (option !== maxOptions - 1) {
                option = 0;
            }
        }

        if (option < 0) {
            option = maxOptions - 1;
        }
        if (option > maxOptions - 1) {
            option = 0;
        }
        option = option % maxOptions;
        return { currentOption: option, selected: keyLPressInstant };
    }

    transitionToNextState() {
        console.log(`-- Ending State: ${this.currentState} --`);
        if (this.stateQueue.length > 0) {
            this.currentState = this.stateQueue.shift();
            console.log(`-- Starting From Queue: ${this.currentState} -- `);
            this.setupActionsForCurrentState();
            return;
        }
        switch (this.currentState) {
            case WILD_BATTLE_INTRO:
                this.currentState = PLAYER_SENDS_OUT_POKEMON;
                break;
            case PLAYER_SENDS_OUT_POKEMON:
                this.currentState = CHOOSE_ACTION;
                break;
            case CHOOSE_ACTION:
                if (this.battleOption === 0) {
                    this.currentState = CHOOSE_MOVE;
                } else if (this.battleOption === 1) {
                    this.currentState = BAG_MENU;
                } else if (this.battleOption === 2) {
                    this.currentState = POKEMON_MENU_BATTLE;
                } else if (this.battleOption === 3) {
                    this.currentState = RUN_AWAY;
                }
                break;
            case CHOOSE_MOVE:
                if (this.shouldCancel) {
                    this.currentState = CHOOSE_ACTION;
                    this.shouldCancel = false;
                    break;
                }
                const resolvedMove = this.getPlayerMoveForSlot(this.moveOption);
                if (resolvedMove === null) {
                    this.currentState = CHOOSE_MOVE;
                    break;
                }
                if (resolvedMove.pp === 0) {
                    this.currentState = NO_PP_LEFT_FOR_THIS_MOVE;
                    break;
                }
                this.currentState = PRE_BATTLE_SEQUENCE;
                break;
            case NO_PP_LEFT_FOR_THIS_MOVE:
                this.currentState = CHOOSE_MOVE;
                break;
            case PRE_BATTLE_SEQUENCE: {
                // Decide who moves first
                this.turnQueue = [];
                this.playerMove = this.getPlayerMoveForSlot(this.moveOption);
                this.foeMove = this.calculateFoeMove(this.foePokemon, this.playerPokemon);
                if (this.turnQueue.length === 0) {
                    this.turnQueue.push(
                        ...this.moveOrder(this.playerPokemon, this.foePokemon, this.playerMove, this.foeMove)
                    );
                }

                console.log(this.playerMove);
                console.log(this.foeMove);

                // TODO: This smells awful. We need to restructure the order of operations here
                // Also, in the case where the foe may switch pokemon or apply an item, this won't work
                if (
                    [POKEMON_SWITCHED, ITEM_APPLIED, POKEMON_CAUGHT, CATCH_POKEMON_ATTEMPT].includes(
                        this.stateHistory[1]
                    )
                ) {
                    this.turnQueue = [this.foePokemon];
                }

                const nextTurn = this.turnQueue.shift();
                if (nextTurn === this.playerPokemon) {
                    this.currentState = BATTLE_SEQUENCE_PLAYER_MOVE;
                } else {
                    this.currentState = BATTLE_SEQUENCE_FOE_MOVE;
                }
                break;
            }
            case BATTLE_SEQUENCE_PLAYER_MOVE:
                if (this.foePokemon.stats.hp === 0 && this.playerPokemon.stats.hp === 0) {
                    this.stateQueue.push(FOE_FAINTS);
                    this.stateQueue.push(PLAYER_FAINTS);
                    break;
                }
                if (this.playerPokemon.stats.hp === 0) {
                    this.stateQueue.push(PLAYER_FAINTS);
                    break;
                }
                if (this.turnQueue.length === 0) {
                    this.currentState = END_BATTLE_SEQUENCE;
                    break;
                }
                if (this.foePokemon.isForcedOut()) {
                    this.currentState = END_BATTLE_SEQUENCE;
                    break;
                }
                this.currentState = BATTLE_SEQUENCE_FOE_MOVE;
                this.turnQueue.shift();
                break;
            case BATTLE_SEQUENCE_FOE_MOVE:
                if (this.foePokemon.stats.hp === 0 && this.playerPokemon.stats.hp === 0) {
                    this.stateQueue.push(PLAYER_FAINTS);
                    this.stateQueue.push(FOE_FAINTS);
                    break;
                }
                if (this.foePokemon.stats.hp === 0) {
                    this.stateQueue.push(FOE_FAINTS);
                    break;
                }
                if (this.turnQueue.length === 0) {
                    this.currentState = END_BATTLE_SEQUENCE;
                    break;
                }
                this.currentState = BATTLE_SEQUENCE_PLAYER_MOVE;
                this.turnQueue.shift();
                break;
            case END_BATTLE_SEQUENCE:
                if (this.foePokemon.stats.hp === 0 && this.playerPokemon.stats.hp === 0) {
                    this.stateQueue.push(FOE_FAINTS);
                    this.stateQueue.push(PLAYER_FAINTS);
                    break;
                }
                if (this.foePokemon.stats.hp === 0) {
                    this.currentState = FOE_FAINTS;
                    break;
                }
                if (this.playerPokemon.stats.hp === 0) {
                    this.currentState = PLAYER_FAINTS;
                    break;
                }
                this.currentState = CHOOSE_ACTION;
                break;
            case FOE_FAINTS:
                this.currentState = SETUP_EXPERIENCE_GAIN_QUEUE;
                break;
            case SETUP_EXPERIENCE_GAIN_QUEUE:
                this.currentState = GAIN_EXPERIENCE_NOTIFICATION;
                break;
            case GAIN_EXPERIENCE_NOTIFICATION:
                this.currentState = GAIN_EXPERIENCE_ACTIVE_POKEMON;
                break;
            case GAIN_EXPERIENCE_ACTIVE_POKEMON:
                if (this.experienceGainQueue[0].experienceGained === 0) {
                    // No more experience to gain, move to next pokemon
                    this.experienceGainQueue.shift();
                    if (this.experienceGainQueue.length === 0) {
                        // Foe pokemon fainted. Check if there is another pokemon to battle before ending it
                        if (this.foePokemonQueue.length > 0) {
                            // TODO: Implement smarter logic for chosing next pokemon
                            this.currentState = TRAINER_SWITCHES_POKEMON;
                            this.stateQueue.push(TRAINER_SENDS_OUT_POKEMON);
                            this.stateQueue.push(CHOOSE_ACTION);
                            break;
                        } else {
                            if (this.foeTrainer !== null) {
                                this.currentState = END_TRAINER_BATTLE;
                            } else {
                                this.currentState = END_BATTLE;
                            }
                        }
                    } else {
                        this.currentState = GAIN_EXPERIENCE_NOTIFICATION;
                    }
                } else {
                    // Still more experience to gain, but we leveled up
                    // TODO: Transition to learn new move flow
                    this.currentState = LEVEL_UP_POKEMON;
                }
                break;
            case LEVEL_UP_POKEMON:
                if (this.experienceGainQueue[0].pokemon.hasNewMoveAvailable()) {
                    this.currentState = NEW_MOVE_AVAILABLE;
                    break;
                }
                this.currentState = GAIN_EXPERIENCE_ACTIVE_POKEMON;
                break;
            case NEW_MOVE_AVAILABLE:
                // If there is space, learn move.
                // else, current moves full
                if (this.experienceGainQueue[0].pokemon.hasEmptyMoveSlot()) {
                    this.currentState = LEARNED_NEW_MOVE;
                } else {
                    this.currentState = CURRENT_MOVES_FULL;
                }
                break;
            case CURRENT_MOVES_FULL:
                if (this.yesOrNoOption === 0) {
                    this.currentState = CHOOSE_MOVE_TO_FORGET;
                } else {
                    this.currentState = STOP_LEARNING_NEW_MOVE;
                }

                break;
            case STOP_LEARNING_NEW_MOVE:
                // TODO: IMPLEMENT
                // If yes, did not learn new move
                // If not, new move available
                if (this.yesOrNoOption === 0) {
                    this.currentState = DID_NOT_LEARN_NEW_MOVE;
                } else {
                    this.currentState = NEW_MOVE_AVAILABLE;
                }

                break;
            case CHOOSE_MOVE_TO_FORGET:
                // TODO: IMPLEMENT
                if (this.shouldCancel) {
                    this.currentState = STOP_LEARNING_NEW_MOVE;
                    this.shouldCancel = false;
                    break;
                }
                this.currentState = FORGOT_MOVE;
                // If cancel, stop learning new move?
                // If move selected, forgot move

                break;
            case FORGOT_MOVE:
                // No condition. Then, learned new move
                this.currentState = LEARNED_NEW_MOVE;

                break;
            case LEARNED_NEW_MOVE:
                // No condition. Then, continue gaining experience.
                if (this.experienceGainQueue[0].pokemon.newMoves.length > 0) {
                    this.currentState = NEW_MOVE_AVAILABLE;
                    break;
                }
                this.currentState = GAIN_EXPERIENCE_ACTIVE_POKEMON;
                break;
            case DID_NOT_LEARN_NEW_MOVE:
                // No condition. Then, continue gaining experience.
                if (this.experienceGainQueue[0].pokemon.newMoves.length > 0) {
                    this.currentState = NEW_MOVE_AVAILABLE;
                    break;
                }
                this.currentState = GAIN_EXPERIENCE_ACTIVE_POKEMON;

                break;
            case PLAYER_FAINTS: {
                const availablePokemon = [];
                this.player.party.forEach((pokemon) => {
                    if (pokemon.stats.hp > 0) {
                        availablePokemon.push(pokemon);
                    }
                });
                if (availablePokemon.length === 0) {
                    this.currentState = WHITED_OUT;
                } else if (this.isTrainerBattle()) {
                    this.currentState = POKEMON_MENU_BATTLE;
                } else {
                    this.currentState = CHOOSE_NEXT_POKEMON_QUESTION;
                }
                break;
            }
            case WHITED_OUT:
                this.currentState = END_BATTLE;
                break;
            case END_BATTLE:
                break;
            case RUN_AWAY:
                if (this.foeTrainer !== null) {
                    // Can't run away from a trainer battle
                    this.currentState = CHOOSE_ACTION;
                    break;
                }
                this.currentState = END_BATTLE;
                break;
            case CHOOSE_NEXT_POKEMON_QUESTION:
                if (this.yesOrNoOption === 0 || this.isTrainerBattle()) {
                    this.currentState = POKEMON_MENU_BATTLE;
                } else {
                    this.currentState = RUN_AWAY;
                }
                break;
            case POKEMON_MENU_BATTLE:
                if (this.shouldCancel) {
                    this.currentState = CHOOSE_ACTION;
                    this.shouldCancel = false;
                    break;
                }
                if (this.pokemonOption === this.player.party.length) {
                    this.currentState = CHOOSE_ACTION;
                } else {
                    this.currentState = POKEMON_PRESELECTED;
                }
                break;
            case POKEMON_PRESELECTED:
                // Decide between pokemon switch phase, pokemon summary, and cancel (back to phase 9)
                if (this.shouldCancel) {
                    this.currentState = POKEMON_MENU_BATTLE;
                    this.shouldCancel = false;
                    break;
                }
                if (this.pokemonDetailOption === 0) {
                    if (this.player.party[this.pokemonOption].stats.hp === 0) {
                        this.currentState = SWITCHED_TO_FAINTED_POKEMON;
                        break;
                    }
                    if (this.playerPokemon === this.player.party[this.pokemonOption]) {
                        this.currentState = SWITCHED_TO_SAME_POKEMON;
                        break;
                    }
                    if (this.playerPokemon.stats.hp > 0) {
                        // Player pokemon just fainted,
                        this.turnQueue = [this.foePokemon];
                        this.currentState = POKEMON_SWITCHED;
                    } else {
                        this.currentState = POKEMON_SWITCHED_END_OF_TURN;
                    }
                } else if (this.pokemonDetailOption === 1) {
                    this.currentState = SUMMARY_MENU_BATTLE;
                } else if (this.pokemonDetailOption === 2) {
                    this.currentState = POKEMON_MENU_BATTLE;
                }
                break;
            case SWITCHED_TO_SAME_POKEMON:
                this.currentState = POKEMON_MENU_BATTLE;
                break;
            case SWITCHED_TO_FAINTED_POKEMON:
                this.currentState = POKEMON_MENU_BATTLE;
                break;
            case POKEMON_SWITCHED:
                // this.turnQueue = [this.foePokemon]
                this.currentState = PRE_BATTLE_SEQUENCE;
                break;
            case POKEMON_SWITCHED_END_OF_TURN:
                this.currentState = END_BATTLE_SEQUENCE;
                break;
            case SUMMARY_MENU_BATTLE:
                if (this.shouldCancel) {
                    this.currentState = POKEMON_PRESELECTED;
                    this.shouldCancel = false;
                    break;
                }
                if (this.summaryOption === 2) {
                    this.currentState = SUMMARY_MOVE_DETAIL;
                } else {
                    this.currentState = POKEMON_MENU_BATTLE;
                }
                break;
            case SUMMARY_MOVE_DETAIL:
                if (this.shouldCancel) {
                    this.currentState = SUMMARY_MENU_BATTLE;
                    this.shouldCancel = false;
                    break;
                }
                this.currentState = SUMMARY_MENU_BATTLE;
                break;
            case BAG_MENU:
                if (this.shouldCancel) {
                    this.currentState = CHOOSE_ACTION;
                    this.shouldCancel = false;
                    break;
                }
                this.currentState = BAG_ITEM_PRESELECTED;
                break;
            case BAG_ITEM_PRESELECTED:
                if (this.selectedItem.battleOptions[this.bagDetailOption] === 'Cancel') {
                    this.shouldCancel = true;
                }
                if (this.shouldCancel) {
                    this.currentState = BAG_MENU;
                    this.shouldCancel = false;
                    break;
                }
                // Player decided to use the item
                // this.selectedItem is already set in BAG_ITEM_PRESELECTED startAction,
                // but update it here to ensure it's current from bagMenu state
                this.selectedItem = this.bagMenu.createItemFromCurrentOption();

                if (battleItemIds.includes(this.selectedItem.id)) {
                    const activeIndex = this.player.party.indexOf(this.playerPokemon);
                    this.pokemonOption = activeIndex >= 0 ? activeIndex : 0;
                    if (this.playerPokemon.canApplyItem(this.selectedItem)) {
                        this.currentState = APPLY_ITEM_TO_POKEMON;
                    } else {
                        this.currentState = IT_WONT_HAVE_ANY_EFFECT;
                    }
                } else if (
                    healingItemIDs.includes(this.selectedItem.id) ||
                    moveItemIDs.includes(this.selectedItem.id) ||
                    (berryItemIds.includes(this.selectedItem.id) &&
                        ((this.selectedItem.healAmount !== null &&
                            this.selectedItem.healAmount !== undefined) ||
                            this.selectedItem.healStatus?.length > 0 ||
                            pinchBerryItemIds.includes(this.selectedItem.id)))
                ) {
                    this.currentState = SELECT_POKEMON_TO_APPLY_ITEM;
                } else if (pokeballItemIDs.includes(this.selectedItem.id)) {
                    if (this.isTrainerBattle()) {
                        this.currentState = DONT_BE_A_THIEF;
                    } else {
                        this.currentState = CATCH_POKEMON_ATTEMPT;
                    }
                }
                break;
            case DONT_BE_A_THIEF:
                this.currentState = BAG_MENU;
                break;
            case SELECT_MOVE_TO_APPLY_ITEM:
                this.currentState = APPLY_ITEM_TO_POKEMON;
                break;
            case SELECT_POKEMON_TO_APPLY_ITEM:
                if (this.shouldCancel) {
                    this.currentState = BAG_MENU;
                    this.shouldCancel = false;
                    break;
                }
                if (this.pokemonOption === this.player.party.length) {
                    // We canceled out of the pokemon menu
                    this.currentState = BAG_MENU;
                } else {
                    // We apply the item to the pokemon
                    // If item has effect, apply the item
                    // If item has no effect, display no effect message
                    // this.selectedItem should already be set from BAG_ITEM_PRESELECTED state
                    const item = this.selectedItem;
                    if (moveItemIDs.includes(item.id)) {
                        this.currentState = SELECT_MOVE_TO_APPLY_ITEM;
                        break;
                    }
                    if (this.player.party[this.pokemonOption].canApplyItem(item)) {
                        this.currentState = APPLY_ITEM_TO_POKEMON;
                        break;
                    } else {
                        this.currentState = IT_WONT_HAVE_ANY_EFFECT;
                    }
                }
                break;
            case APPLY_ITEM_TO_POKEMON:
                this.turnQueue = [this.foePokemon];
                this.currentState = ITEM_APPLIED;
                break;
            case IT_WONT_HAVE_ANY_EFFECT:
                this.currentState = BAG_MENU;
                break;
            case ITEM_APPLIED:
                this.currentState = PRE_BATTLE_SEQUENCE;
                break;
            case CATCH_POKEMON_ATTEMPT:
                if (this.foePokemon.isWild()) {
                    this.turnQueue = [this.foePokemon];
                    this.currentState = PRE_BATTLE_SEQUENCE;
                    break;
                } else {
                    this.currentState = POKEMON_CAUGHT;
                }
                break;
            case POKEMON_CAUGHT:
                if (this.player.pokedex.accessible && !this.player.pokedex.isInPokedex(this.foePokemon)) {
                    this.currentState = ADD_CAUGHT_POKEMON_TO_POKEDEX;
                } else {
                    this.currentState = ASK_FOR_NICKNAME;
                }
                this.player.acquirePokemon(this.foePokemon);
                getOSM().context.keyboardTargetPokemon = this.foePokemon;
                break;
            case ADD_CAUGHT_POKEMON_TO_POKEDEX:
                this.currentState = ASK_FOR_NICKNAME;
                break;
            case ASK_FOR_NICKNAME:
                // TODO: Implement the nickname screen flow.
                if (this.currentOption === 0) {
                    this.currentState = ASK_FOR_NICKNAME;
                } else {
                    if (this.player.party.includes(this.foePokemon)) {
                        this.currentState = END_BATTLE;
                    } else {
                        this.currentState = POKEMON_SENT_TO_PC;
                    }
                }
                break;
            case POKEMON_SENT_TO_PC:
                this.currentState = END_BATTLE;
                break;
            case NICKNAME_INPUT:
                this.currentState = NICKNAME_CONFIRMATION;
                break;
            case NICKNAME_CONFIRMATION:
                if (this.currentOption === 0) {
                    this.currentState = END_BATTLE;
                } else {
                    this.currentState = NICKNAME_INPUT;
                }
                break;
            case TRAINER_BATTLE_INTRO:
                this.currentState = TRAINER_WOULD_LIKE_TO_BATTLE;
                break;
            case TRAINER_SWITCHES_POKEMON:
                this.currentState = TRAINER_SENDS_OUT_POKEMON;
                break;
            case TRAINER_WOULD_LIKE_TO_BATTLE:
                this.currentState = TRAINER_SENDS_OUT_POKEMON;
                break;
            case TRAINER_SENDS_OUT_POKEMON:
                this.currentState = PLAYER_SENDS_OUT_POKEMON;
                break;
            case TRAINER_POKEMON_FORCED_OUT:
                this.currentState = FOE_POKEMON_SENT_OUT_END;
                break;
            case PLAYER_POKEMON_FORCED_OUT:
                this.currentState = PLAYER_POKEMON_SENT_OUT_END;
                break;
            case PLAYER_POKEMON_SENT_OUT_END:
                this.currentState = END_BATTLE_SEQUENCE;
                break;
            case FOE_POKEMON_SENT_OUT_END:
                this.currentState = END_BATTLE_SEQUENCE;
                break;
            case END_TRAINER_BATTLE:
                this.currentState = END_BATTLE;
                break;
        }
        this.setupActionsForCurrentState(); // Setup actions for the new state
    }
}
